import {
  Check,
  ChevronDown,
  ChevronLeft,
  Eye,
  Link as LinkIcon,
  Loader2,
  Pencil,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import customizationIcon from '@/assets/icons/customization.svg';
import deleteIcon from '@/assets/icons/delete.svg';
import publishIcon from '@/assets/icons/publish.svg';
import settingsIcon from '@/assets/icons/settings.svg';
import { AppShell } from '@/components/layout/AppShell';
import { ConfirmDialog } from '@/components/patterns/ConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MaskIcon } from '@/components/ui/MaskIcon';
import { useGetCustomization } from '@/features/customization/api/useGetCustomization';
import { useGetWorkflowCustomization } from '@/features/customization/api/useWorkflowCustomization';
import { WorkflowCustomizationDrawer } from '@/features/customization/components/WorkflowCustomizationDrawer';
import { useGoogleFont } from '@/features/customization/hooks/useGoogleFont';
import {
  parseCustomization,
  toBrandCssVars,
} from '@/features/customization/schemas/customizationSchemas';
import { QuestionnaireStepPreview } from '@/features/questionnaires/components/QuestionnaireStepPreview';
import { parseQuestionnaireStepSettings } from '@/features/questionnaires/lib/questionnaireStepSettings';
import { useCopyWorkflowLink } from '@/features/sessions/api/useCopyWorkflowLink';
import { esignStepPrice, parseEsignStepSettings } from '@/features/templates/lib/esignStepSettings';
import {
  parseTemplateStepSettings,
  writeTemplateStepSettings,
} from '@/features/templates/lib/templateStepSettings';
import { toast } from '@/lib/notify';
import { cn } from '@/lib/utils';
import { useUiStore } from '@/stores/uiStore';

import { useApiCatalogue } from '../api/useApiCatalogue';
import { useListWorkflows } from '../api/useListWorkflows';
import { usePermanentDeleteWorkflow } from '../api/usePermanentDeleteWorkflow';
import { usePublishWorkflow } from '../api/usePublishWorkflow';
import { useSaveWorkflowFull } from '../api/useSaveWorkflowFull';
import { useWorkflow } from '../api/useWorkflow';
import {
  firstScreenIndexForStep,
  hasPreviewScreen,
  type JourneyScreen,
  MobilePreview,
} from '../components/simple/MobilePreview';
import {
  EMAIL_VERIFICATION_CATEGORY,
  EmailOtpScreen,
  EmailVerificationScreen,
  QUESTIONNAIRE_CATEGORY,
  type WelcomePreviewStep,
} from '../components/simple/preview-screens';
import {
  isRepeatableStepType,
  StepCatalog,
  stepDependencyReason,
  workflowHasFaceMatchPhotoSource,
} from '../components/simple/StepCatalog';
import {
  DEFAULT_STEP_SETTINGS_JSON,
  StepSettingsPanel,
} from '../components/simple/StepSettingsPanel';
import {
  DEFAULT_WORKFLOW_SETTINGS,
  parseWorkflowSettings,
  resolveWelcomeCopy,
  type WorkflowSettings,
  WorkflowSettingsDrawer,
} from '../components/WorkflowSettingsDrawer';
import type { WorkflowStepDto } from '../schemas/workflowSchemas';
import { stepLabel } from '../stepIcons';
import { STEP_TYPE_CATALOGUE_TAGS, STEP_TYPE_META } from '../types';

/**
 * Steps whose settings replace the phone preview rather than sit beside it.
 *
 * Both are document work with no applicant screen to mirror — a document is
 * generated server-side, and signature placement is an admin task — and both need
 * the width the phone would take.
 */
const STEPS_WITHOUT_PREVIEW = new Set<string>(['document_template', 'esign_template']);

// The editor route for a workflow — list-based "simple" vs node "graph".
// Same rule the workflows list uses to pick a row's destination.
const editorHref = (w: { id: string; structure: string }) =>
  w.structure === 'Simple' ? `/workflows/${w.id}/simple` : `/workflows/${w.id}/editor`;

// Default PAN Risk Rules — actions + thresholds set per the SEBI spec (PDF §4.3):
//   Inactive PAN Status      → Reject   (an inactive/invalid PAN can't onboard)
//   PAN-Aadhaar Linking      → Review   (not-linked flagged for manual review)
//   PAN already exists       → Reject   (deduplication → reject onboarding)
//   Age Restriction          → Reject   when age < 18 (min age configurable)
//   Name Match Score         → Review   when score < 70 (threshold configurable)
const createDefaultPanRiskRules = () => [
  {
    id: crypto.randomUUID(),
    ruleKey: 'pan_status',
    isEnabled: true,
    action: 'Reject',
    config: null,
  },
  {
    id: crypto.randomUUID(),
    ruleKey: 'pan_aadhaar_link',
    isEnabled: true,
    action: 'Review',
    config: null,
  },
  {
    id: crypto.randomUUID(),
    ruleKey: 'pan_exists',
    isEnabled: true,
    action: 'Reject',
    config: null,
  },
  {
    id: crypto.randomUUID(),
    ruleKey: 'age_restriction',
    isEnabled: true,
    action: 'Reject',
    config: '18',
  },
  {
    id: crypto.randomUUID(),
    ruleKey: 'name_match_score',
    isEnabled: true,
    action: 'Review',
    config: '70',
  },
];

// Default Aadhaar Risk Rules — set per the SEBI spec (PDF §5.3):
//   Age Restriction          → Reject   when age (from Aadhaar) < 18 (configurable)
//   Name Mismatch (vs PAN)    → Review   when Aadhaar↔PAN name score < 80 (configurable)
const createDefaultAadhaarRiskRules = () => [
  {
    id: crypto.randomUUID(),
    ruleKey: 'age_restriction',
    isEnabled: true,
    action: 'Reject',
    config: '18',
  },
  {
    id: crypto.randomUUID(),
    ruleKey: 'name_match_score',
    isEnabled: true,
    action: 'Review',
    config: '80',
  },
];

// Default PAN Return Data — the full field set + defaults per the SEBI spec
// (PDF §4.5). Four groups: Identity Details, Personal Information, Risk Signals,
// Metadata. Every field is "Returnable by Default: Yes" in the spec, so all ship
// ON; the admin can toggle any OFF to exclude it from the result + dashboard.
// NOTE: some fields are Studio-computed / not returned verbatim by every PAN API
// (age is derived from DOB; duplicate_pan_flag / verification_status / verified_at
// / method_used are Studio-side) — the result-building step populates these.
const PAN_RETURN_FIELD_KEYS = [
  'pan_number',
  'pan_status',
  'pan_holder_name',
  'date_of_birth',
  'pan_category',
  'age',
  'name_match_score',
  'duplicate_pan_flag',
  'verification_status',
  'verified_at',
  'method_used',
  'verification_attempts',
  'session_id',
] as const;

const createDefaultPanReturnFields = () => [
  // ── Identity Details ──────────────────────────────────────────────────────
  {
    id: crypto.randomUUID(),
    fieldKey: 'pan_number',
    fieldGroup: 'Identity Details',
    displayLabel: 'PAN Number',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'pan_status',
    fieldGroup: 'Identity Details',
    displayLabel: 'PAN Status',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  // ── Personal Information ──────────────────────────────────────────────────
  {
    id: crypto.randomUUID(),
    fieldKey: 'pan_holder_name',
    fieldGroup: 'Personal Information',
    displayLabel: 'PAN Holder Name',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'date_of_birth',
    fieldGroup: 'Personal Information',
    displayLabel: 'Date of Birth',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'pan_category',
    fieldGroup: 'Personal Information',
    displayLabel: 'PAN Category',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'age',
    fieldGroup: 'Personal Information',
    displayLabel: 'Age',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  // ── Risk Signals ──────────────────────────────────────────────────────────
  {
    id: crypto.randomUUID(),
    fieldKey: 'name_match_score',
    fieldGroup: 'Risk Signals',
    displayLabel: 'Name Match Score',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'duplicate_pan_flag',
    fieldGroup: 'Risk Signals',
    displayLabel: 'Duplicate PAN Flag',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  // ── Metadata ──────────────────────────────────────────────────────────────
  {
    id: crypto.randomUUID(),
    fieldKey: 'verification_status',
    fieldGroup: 'Metadata',
    displayLabel: 'Status',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'verified_at',
    fieldGroup: 'Metadata',
    displayLabel: 'Verified At',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'method_used',
    fieldGroup: 'Metadata',
    displayLabel: 'Method Used',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'verification_attempts',
    fieldGroup: 'Metadata',
    displayLabel: 'Verification Attempts',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'session_id',
    fieldGroup: 'Metadata',
    displayLabel: 'Session ID',
    isIncluded: true,
    isDefaultIncluded: true,
  },
];

// Default Aadhaar Return Data — the field set + defaults per the SEBI spec
// (PDF §5.5). The demographic field KEYS match the actual Aadhaar Secure QR API
// response (AadhaarSecureQrResponse.fields.*) so the Return Data filter and the
// Advanced Rules "Field" dropdown line up with real response keys — the condition
// engine matches by leaf key name (name/dob/gender/pincode/…). Mobile & email are
// OFF by default (privacy), everything else ON. The Risk Signals group holds
// Studio-computed metadata (name_match_score vs PAN, verified_at, …).
const createDefaultAadhaarReturnFields = () => [
  {
    id: crypto.randomUUID(),
    fieldKey: 'reference_id',
    fieldGroup: 'Identity Details',
    displayLabel: 'Aadhaar Reference ID',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'name',
    fieldGroup: 'Identity Details',
    displayLabel: 'Name on Aadhaar',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'dob',
    fieldGroup: 'Personal Information',
    displayLabel: 'Date of Birth',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'gender',
    fieldGroup: 'Personal Information',
    displayLabel: 'Gender',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'address',
    fieldGroup: 'Personal Information',
    displayLabel: 'Address',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'pincode',
    fieldGroup: 'Personal Information',
    displayLabel: 'PIN Code',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'state',
    fieldGroup: 'Personal Information',
    displayLabel: 'State',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'masked_mobile',
    fieldGroup: 'Personal Information',
    displayLabel: 'Mobile No.',
    isIncluded: false,
    isDefaultIncluded: false,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'masked_email',
    fieldGroup: 'Personal Information',
    displayLabel: 'Email Id',
    isIncluded: false,
    isDefaultIncluded: false,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'name_match_score',
    fieldGroup: 'Risk Signals',
    displayLabel: 'Name Match Score vs PAN',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'verified_at',
    fieldGroup: 'Risk Signals',
    displayLabel: 'Verified At',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'method_used',
    fieldGroup: 'Risk Signals',
    displayLabel: 'Method Used',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'consent_captured_at',
    fieldGroup: 'Risk Signals',
    displayLabel: 'Consent Timestamp',
    isIncluded: true,
    isDefaultIncluded: true,
  },
];

// Default Face Match Risk Rules — aligned to the real FaceMatch/verify API
// (matchScore / match / reviewNeeded; no spoof field):
//   Face Match Fail → Review   when match score < 80 (threshold configurable)
//   Review Needed   → Review   when the API flags the result for manual review
const createDefaultFaceRiskRules = () => [
  {
    id: crypto.randomUUID(),
    ruleKey: 'face_match_fail',
    isEnabled: true,
    action: 'Review',
    config: '80',
  },
  {
    id: crypto.randomUUID(),
    ruleKey: 'review_needed',
    isEnabled: true,
    action: 'Review',
    config: null,
  },
];

// Default Liveness Risk Rules — aligned to the real FaceLiveness API
// (isLive / livenessScore / reviewNeeded / multipleFacesDetected; no duplicate):
//   Liveness Fail            → Reject   when liveness score < 85 (configurable)
//   Multiple Faces Detected  → Review   (action configurable)
//   Review Needed            → Review   (action configurable)
const createDefaultLivenessRiskRules = () => [
  {
    id: crypto.randomUUID(),
    ruleKey: 'liveness_score',
    isEnabled: true,
    action: 'Reject',
    config: '85',
  },
  {
    id: crypto.randomUUID(),
    ruleKey: 'multiple_faces',
    isEnabled: true,
    action: 'Review',
    config: null,
  },
  {
    id: crypto.randomUUID(),
    ruleKey: 'review_needed',
    isEnabled: true,
    action: 'Review',
    config: null,
  },
];

// Default Face Match Return Data — aligned to the real FaceMatch/verify response
// (matchScore, match, confidence, reviewNeeded, brightness, sharpness).
// capture_attempts / verification_status / verified_at / method_used are
// Studio-computed by the result normalizer.
const createDefaultFaceReturnFields = () => [
  {
    id: crypto.randomUUID(),
    fieldKey: 'face_matched',
    fieldGroup: 'Verification Results',
    displayLabel: 'Face Matched',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'face_match_score',
    fieldGroup: 'Verification Results',
    displayLabel: 'Face Match Score',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'confidence',
    fieldGroup: 'Verification Results',
    displayLabel: 'Confidence',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'brightness',
    fieldGroup: 'Verification Results',
    displayLabel: 'Brightness',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'sharpness',
    fieldGroup: 'Verification Results',
    displayLabel: 'Sharpness',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'review_needed',
    fieldGroup: 'Risk Signals',
    displayLabel: 'Review Needed',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'capture_attempts',
    fieldGroup: 'Metadata',
    displayLabel: 'Capture Attempts',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'verification_status',
    fieldGroup: 'Metadata',
    displayLabel: 'Status',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'verified_at',
    fieldGroup: 'Metadata',
    displayLabel: 'Verified At',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'method_used',
    fieldGroup: 'Metadata',
    displayLabel: 'Method Used',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'session_id',
    fieldGroup: 'Metadata',
    displayLabel: 'Session ID',
    isIncluded: true,
    isDefaultIncluded: true,
  },
];

// Default Liveness Return Data — aligned to the real FaceLiveness response
// (isLive, livenessScore, reviewNeeded, multipleFacesDetected).
const createDefaultLivenessReturnFields = () => [
  {
    id: crypto.randomUUID(),
    fieldKey: 'liveness_passed',
    fieldGroup: 'Verification Results',
    displayLabel: 'Liveness Passed',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'liveness_score',
    fieldGroup: 'Verification Results',
    displayLabel: 'Liveness Score',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'multiple_faces_detected',
    fieldGroup: 'Risk Signals',
    displayLabel: 'Multiple Faces Detected',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'review_needed',
    fieldGroup: 'Risk Signals',
    displayLabel: 'Review Needed',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'capture_attempts',
    fieldGroup: 'Metadata',
    displayLabel: 'Capture Attempts',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'verification_status',
    fieldGroup: 'Metadata',
    displayLabel: 'Status',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'verified_at',
    fieldGroup: 'Metadata',
    displayLabel: 'Verified At',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'method_used',
    fieldGroup: 'Metadata',
    displayLabel: 'Method Used',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'session_id',
    fieldGroup: 'Metadata',
    displayLabel: 'Session ID',
    isIncluded: true,
    isDefaultIncluded: true,
  },
];

// Default Bank Account Verification Risk Rules (SEBI spec §8.3). Mirrors Aadhaar's
// name-match handling: the name_mismatch rule is seeded here and the return-field
// guard disables it in the UI until an earlier PAN/Aadhaar step provides a name.
//   Account Inactive → Reject   when the bank reports the account INACTIVE
//   Name Mismatch    → Review   when account name vs PAN/Aadhaar best-match < 75
//   Duplicate Account→ Reject   when the same account was verified in an earlier
//                               session (Studio cross-session ledger, like PAN's).
const createDefaultBankRiskRules = () => [
  {
    id: crypto.randomUUID(),
    ruleKey: 'account_inactive',
    isEnabled: true,
    action: 'Reject',
    config: null,
  },
  {
    id: crypto.randomUUID(),
    ruleKey: 'name_mismatch',
    isEnabled: true,
    action: 'Review',
    config: '75',
  },
  {
    id: crypto.randomUUID(),
    ruleKey: 'duplicate_account',
    isEnabled: true,
    action: 'Reject',
    config: null,
  },
];

// Default Bank Return Data (SEBI spec §8.5) — mapped to the real gateway response.
// name_match_score / name_matched_against are Studio-computed (best-of-two vs the
// applicant's verified PAN/Aadhaar name); bank_name only comes from the v2 method.
const createDefaultBankReturnFields = () => [
  {
    id: crypto.randomUUID(),
    fieldKey: 'account_number',
    fieldGroup: 'Bank Details',
    displayLabel: 'Account Number (masked)',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'account_holder_name',
    fieldGroup: 'Bank Details',
    displayLabel: 'Account Holder Name',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'bank_name',
    fieldGroup: 'Bank Details',
    displayLabel: 'Bank Name',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'ifsc',
    fieldGroup: 'Bank Details',
    displayLabel: 'IFSC',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'account_status',
    fieldGroup: 'Bank Details',
    displayLabel: 'Account Status',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'bank_reference',
    fieldGroup: 'Bank Details',
    displayLabel: 'Bank Reference (RRN)',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'name_match_score',
    fieldGroup: 'Verification Results',
    displayLabel: 'Name Match Score',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'name_matched_against',
    fieldGroup: 'Verification Results',
    displayLabel: 'Name Matched Against',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'verification_status',
    fieldGroup: 'Metadata',
    displayLabel: 'Status',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'verification_attempts',
    fieldGroup: 'Metadata',
    displayLabel: 'Verification Attempts',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'verified_at',
    fieldGroup: 'Metadata',
    displayLabel: 'Verified At',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'method_used',
    fieldGroup: 'Metadata',
    displayLabel: 'Method Used',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'session_id',
    fieldGroup: 'Metadata',
    displayLabel: 'Session ID',
    isIncluded: true,
    isDefaultIncluded: true,
  },
];

// Default GST Verification Risk Rules (Studio GST spec). gstin_cancelled is
// intentionally NOT included — the gateway's Basic method never returns a
// cancellation date, and Advance only returns one sometimes (vendor-dependent), so a
// rule that could silently never fire would be worse than no rule.
//   GSTIN Status Not Active     → Reject   when the GSTIN is not Active
//   Business Vintage Restriction→ Review   when registered < 6 months ago
//   Legal Name Match            → Review   when legal name vs PAN best-match < 70
//   GSTIN Already Exists        → Reject   when the same GSTIN was verified in an
//                                 earlier session (Studio cross-session ledger)
const createDefaultGstRiskRules = () => [
  {
    id: crypto.randomUUID(),
    ruleKey: 'gstin_status_not_active',
    isEnabled: true,
    action: 'Reject',
    config: null,
  },
  {
    id: crypto.randomUUID(),
    ruleKey: 'business_vintage_restriction',
    isEnabled: true,
    action: 'Review',
    config: '6',
  },
  {
    id: crypto.randomUUID(),
    ruleKey: 'legal_name_match',
    isEnabled: true,
    action: 'Review',
    config: '70',
  },
  {
    id: crypto.randomUUID(),
    ruleKey: 'gstin_already_exists',
    isEnabled: true,
    action: 'Reject',
    config: null,
  },
];

// Default GST Return Data — mapped to the real gateway response (Whitebooks basic
// GSTIN lookup, the only method Studio offers). nature_of_business_activity,
// additional_place_of_business, date_of_cancellation, last_updated_date and
// e_invoice_status from the PDF spec are NOT included — this method never returns
// them, so there is nothing to toggle.
const createDefaultGstReturnFields = () => [
  {
    id: crypto.randomUUID(),
    fieldKey: 'gst_identification_number',
    fieldGroup: 'Identity Details',
    displayLabel: 'GSTIN',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'gstin_status',
    fieldGroup: 'Identity Details',
    displayLabel: 'GSTIN Status',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'legal_name_of_business',
    fieldGroup: 'Business Information',
    displayLabel: 'Legal Name of Business',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'trade_name',
    fieldGroup: 'Business Information',
    displayLabel: 'Trade Name',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'constitution_of_business',
    fieldGroup: 'Business Information',
    displayLabel: 'Constitution of Business',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'taxpayer_type',
    fieldGroup: 'Business Information',
    displayLabel: 'Taxpayer Type',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'date_of_registration',
    fieldGroup: 'Business Information',
    displayLabel: 'Date of Registration',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'principal_place_of_business_address',
    fieldGroup: 'Address & Jurisdiction Details',
    displayLabel: 'Principal Place of Business',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'state_jurisdiction',
    fieldGroup: 'Address & Jurisdiction Details',
    displayLabel: 'State Jurisdiction',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'center_jurisdiction',
    fieldGroup: 'Address & Jurisdiction Details',
    displayLabel: 'Center Jurisdiction',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'name_match_score',
    fieldGroup: 'Verification Results',
    displayLabel: 'Name Match Score',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'name_matched_against',
    fieldGroup: 'Verification Results',
    displayLabel: 'Name Matched Against',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'duplicate_gstin_flag',
    fieldGroup: 'Verification Results',
    displayLabel: 'Duplicate GSTIN Flag',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'verification_status',
    fieldGroup: 'Metadata',
    displayLabel: 'Status',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'verification_attempts',
    fieldGroup: 'Metadata',
    displayLabel: 'Verification Attempts',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'verified_at',
    fieldGroup: 'Metadata',
    displayLabel: 'Verified At',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'method_used',
    fieldGroup: 'Metadata',
    displayLabel: 'Method Used',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'session_id',
    fieldGroup: 'Metadata',
    displayLabel: 'Session ID',
    isIncluded: true,
    isDefaultIncluded: true,
  },
];

// ── ID Verification (combined Voter ID / Passport / Driving Licence) ──────────
// One step; Risk Rules + Return Data are configured PER document. Every key is
// namespaced "<docType>::<key>" so the engine can scope it to the document that
// ran (SessionRuleEngine strips the prefix and filters by the ran document). The
// Return-Data fieldGroup carries the document label so the builder renders one
// section per document without any grouping-logic change.
const ID_RISK_RULES: Array<{
  ruleKey: string;
  action: string;
  config: string | null;
  isEnabled?: boolean;
}> = [
  // Voter ID
  { ruleKey: 'voter_id::age_restriction', action: 'Reject', config: '18' },
  { ruleKey: 'voter_id::stale_electoral_record', action: 'Review', config: '36' },
  { ruleKey: 'voter_id::epic_already_exists', action: 'Reject', config: null },
  // Passport
  { ruleKey: 'passport::passport_number_mismatch', action: 'Reject', config: null },
  { ruleKey: 'passport::date_of_issue_mismatch', action: 'Review', config: null },
  { ruleKey: 'passport::name_score_low', action: 'Review', config: '70' },
  { ruleKey: 'passport::passport_already_exists', action: 'Reject', config: null },
  // Driving Licence
  { ruleKey: 'driving_license::dl_status_not_active', action: 'Reject', config: null },
  { ruleKey: 'driving_license::enforcement_active', action: 'Reject', config: null },
  // Commercial/PSV is only relevant when the workflow requires driving public
  // passenger carriers — seeded but OFF by default (per spec).
  { ruleKey: 'driving_license::commercial_psv', action: 'Review', config: null, isEnabled: false },
  { ruleKey: 'driving_license::age_restriction', action: 'Reject', config: '18' },
  { ruleKey: 'driving_license::dl_already_exists', action: 'Reject', config: null },
];

const ID_RETURN_FIELDS: Array<{
  fieldKey: string;
  fieldGroup: string;
  displayLabel: string;
  on?: boolean;
}> = [
  // ── Voter ID ──
  {
    fieldKey: 'voter_id::epic_number',
    fieldGroup: 'Voter ID · Identity Details',
    displayLabel: 'EPIC Number',
  },
  {
    fieldKey: 'voter_id::full_name',
    fieldGroup: 'Voter ID · Personal Information',
    displayLabel: 'Full Name',
  },
  {
    fieldKey: 'voter_id::relative_name',
    fieldGroup: 'Voter ID · Personal Information',
    displayLabel: "Relative's Name",
  },
  {
    fieldKey: 'voter_id::relation_type',
    fieldGroup: 'Voter ID · Personal Information',
    displayLabel: 'Relation Type',
  },
  {
    fieldKey: 'voter_id::date_of_birth',
    fieldGroup: 'Voter ID · Personal Information',
    displayLabel: 'Date of Birth',
  },
  {
    fieldKey: 'voter_id::gender',
    fieldGroup: 'Voter ID · Personal Information',
    displayLabel: 'Gender',
  },
  { fieldKey: 'voter_id::age', fieldGroup: 'Voter ID · Personal Information', displayLabel: 'Age' },
  {
    fieldKey: 'voter_id::house_no',
    fieldGroup: 'Voter ID · Address & Constituency Details',
    displayLabel: 'House Number',
  },
  {
    fieldKey: 'voter_id::assembly_constituency',
    fieldGroup: 'Voter ID · Address & Constituency Details',
    displayLabel: 'Assembly Constituency',
  },
  {
    fieldKey: 'voter_id::polling_station',
    fieldGroup: 'Voter ID · Address & Constituency Details',
    displayLabel: 'Polling Station',
  },
  {
    fieldKey: 'voter_id::district',
    fieldGroup: 'Voter ID · Address & Constituency Details',
    displayLabel: 'District',
  },
  {
    fieldKey: 'voter_id::state',
    fieldGroup: 'Voter ID · Address & Constituency Details',
    displayLabel: 'State',
  },
  {
    fieldKey: 'voter_id::pin',
    fieldGroup: 'Voter ID · Address & Constituency Details',
    displayLabel: 'PIN Code',
  },
  {
    fieldKey: 'voter_id::age_restriction_flag',
    fieldGroup: 'Voter ID · Risk Signals',
    displayLabel: 'Age Restriction Flag',
  },
  {
    fieldKey: 'voter_id::duplicate_epic_flag',
    fieldGroup: 'Voter ID · Risk Signals',
    displayLabel: 'Duplicate EPIC Flag',
  },
  {
    fieldKey: 'voter_id::verification_status',
    fieldGroup: 'Voter ID · Metadata',
    displayLabel: 'Status',
  },
  {
    fieldKey: 'voter_id::verified_at',
    fieldGroup: 'Voter ID · Metadata',
    displayLabel: 'Verified At',
  },
  {
    fieldKey: 'voter_id::method_used',
    fieldGroup: 'Voter ID · Metadata',
    displayLabel: 'Method Used',
  },
  {
    fieldKey: 'voter_id::verification_attempts',
    fieldGroup: 'Voter ID · Metadata',
    displayLabel: 'Verification Attempts',
  },
  {
    fieldKey: 'voter_id::last_update',
    fieldGroup: 'Voter ID · Metadata',
    displayLabel: 'Electoral Roll Last Updated',
  },
  // ── Passport ──
  {
    fieldKey: 'passport::passport_number',
    fieldGroup: 'Passport · Identity Details',
    displayLabel: 'Passport Number',
  },
  {
    fieldKey: 'passport::name_from_passport',
    fieldGroup: 'Passport · Personal Information',
    displayLabel: 'Name (from Passport)',
  },
  {
    fieldKey: 'passport::surname_from_passport',
    fieldGroup: 'Passport · Personal Information',
    displayLabel: 'Surname (from Passport)',
  },
  {
    fieldKey: 'passport::date_of_issue',
    fieldGroup: 'Passport · Issue Details',
    displayLabel: 'Date of Issue',
  },
  {
    fieldKey: 'passport::type_of_application',
    fieldGroup: 'Passport · Issue Details',
    displayLabel: 'Type of Application',
  },
  {
    fieldKey: 'passport::application_date',
    fieldGroup: 'Passport · Issue Details',
    displayLabel: 'Application Date',
  },
  {
    fieldKey: 'passport::name_match',
    fieldGroup: 'Passport · Risk Signals',
    displayLabel: 'Name Match',
  },
  {
    fieldKey: 'passport::name_score',
    fieldGroup: 'Passport · Risk Signals',
    displayLabel: 'Name Match Score',
  },
  {
    fieldKey: 'passport::passport_number_match',
    fieldGroup: 'Passport · Risk Signals',
    displayLabel: 'Passport Number Match',
  },
  {
    fieldKey: 'passport::date_of_issue_match',
    fieldGroup: 'Passport · Risk Signals',
    displayLabel: 'Date of Issue Match',
  },
  {
    fieldKey: 'passport::verification_status',
    fieldGroup: 'Passport · Metadata',
    displayLabel: 'Status',
  },
  {
    fieldKey: 'passport::verified_at',
    fieldGroup: 'Passport · Metadata',
    displayLabel: 'Verified At',
  },
  {
    fieldKey: 'passport::method_used',
    fieldGroup: 'Passport · Metadata',
    displayLabel: 'Method Used',
  },
  {
    fieldKey: 'passport::verification_attempts',
    fieldGroup: 'Passport · Metadata',
    displayLabel: 'Verification Attempts',
  },
  // ── Driving Licence (aligned to the real gateway response schema) ──
  {
    fieldKey: 'driving_license::dl_number',
    fieldGroup: 'Driving Licence · Identity Details',
    displayLabel: 'DL Number',
  },
  {
    fieldKey: 'driving_license::dl_status',
    fieldGroup: 'Driving Licence · Identity Details',
    displayLabel: 'DL Status',
  },
  {
    fieldKey: 'driving_license::issuing_state',
    fieldGroup: 'Driving Licence · Identity Details',
    displayLabel: 'State',
  },
  {
    fieldKey: 'driving_license::full_name',
    fieldGroup: 'Driving Licence · Personal Information',
    displayLabel: 'Full Name',
  },
  {
    fieldKey: 'driving_license::father_or_spouse_name',
    fieldGroup: 'Driving Licence · Personal Information',
    displayLabel: "Father's / Spouse's Name",
  },
  {
    fieldKey: 'driving_license::date_of_birth',
    fieldGroup: 'Driving Licence · Personal Information',
    displayLabel: 'Date of Birth',
  },
  {
    fieldKey: 'driving_license::age',
    fieldGroup: 'Driving Licence · Personal Information',
    displayLabel: 'Age',
  },
  {
    fieldKey: 'driving_license::blood_group',
    fieldGroup: 'Driving Licence · Personal Information',
    displayLabel: 'Blood Group',
  },
  {
    fieldKey: 'driving_license::permanent_address',
    fieldGroup: 'Driving Licence · Personal Information',
    displayLabel: 'Permanent Address',
  },
  {
    fieldKey: 'driving_license::district',
    fieldGroup: 'Driving Licence · Personal Information',
    displayLabel: 'District',
  },
  {
    fieldKey: 'driving_license::vehicle_class',
    fieldGroup: 'Driving Licence · License & Coverage Details',
    displayLabel: 'Vehicle Class / Category',
  },
  {
    fieldKey: 'driving_license::license_issue_date',
    fieldGroup: 'Driving Licence · License & Coverage Details',
    displayLabel: 'License Issue Date',
  },
  {
    fieldKey: 'driving_license::non_transport_validity',
    fieldGroup: 'Driving Licence · License & Coverage Details',
    displayLabel: 'Non-Transport Validity',
  },
  {
    fieldKey: 'driving_license::transport_validity',
    fieldGroup: 'Driving Licence · License & Coverage Details',
    displayLabel: 'Transport Validity',
  },
  {
    fieldKey: 'driving_license::validity_type',
    fieldGroup: 'Driving Licence · License & Coverage Details',
    displayLabel: 'Validity Type',
  },
  {
    fieldKey: 'driving_license::enforcement_status',
    fieldGroup: 'Driving Licence · Risk Signals',
    displayLabel: 'Enforcement Status',
  },
  {
    fieldKey: 'driving_license::duplicate_dl_flag',
    fieldGroup: 'Driving Licence · Risk Signals',
    displayLabel: 'Duplicate DL Flag',
  },
  {
    fieldKey: 'driving_license::initial_issuing_office',
    fieldGroup: 'Driving Licence · Endorsement & Hazardous Details',
    displayLabel: 'Initial Issuing Office',
  },
  {
    fieldKey: 'driving_license::last_endorsement_date',
    fieldGroup: 'Driving Licence · Endorsement & Hazardous Details',
    displayLabel: 'Last Endorsement Date',
  },
  {
    fieldKey: 'driving_license::last_endorsed_office',
    fieldGroup: 'Driving Licence · Endorsement & Hazardous Details',
    displayLabel: 'Last Endorsed Office',
  },
  {
    fieldKey: 'driving_license::endorsement_reason',
    fieldGroup: 'Driving Licence · Endorsement & Hazardous Details',
    displayLabel: 'Endorsement Reason',
  },
  {
    fieldKey: 'driving_license::hazardous_valid_till',
    fieldGroup: 'Driving Licence · Endorsement & Hazardous Details',
    displayLabel: 'Hazardous Valid Till',
  },
  {
    fieldKey: 'driving_license::hill_valid_till',
    fieldGroup: 'Driving Licence · Endorsement & Hazardous Details',
    displayLabel: 'Hill Valid Till',
  },
  {
    fieldKey: 'driving_license::verification_status',
    fieldGroup: 'Driving Licence · Metadata',
    displayLabel: 'Status',
  },
  {
    fieldKey: 'driving_license::verified_at',
    fieldGroup: 'Driving Licence · Metadata',
    displayLabel: 'Verified At',
  },
  {
    fieldKey: 'driving_license::method_used',
    fieldGroup: 'Driving Licence · Metadata',
    displayLabel: 'Method Used',
  },
  {
    fieldKey: 'driving_license::verification_attempts',
    fieldGroup: 'Driving Licence · Metadata',
    displayLabel: 'Verification Attempts',
  },
];

const createDefaultIdRiskRules = () =>
  ID_RISK_RULES.map((r) => ({
    id: crypto.randomUUID(),
    ruleKey: r.ruleKey,
    isEnabled: r.isEnabled ?? true,
    action: r.action,
    config: r.config,
  }));

const createDefaultIdReturnFields = () =>
  ID_RETURN_FIELDS.map((f) => ({
    id: crypto.randomUUID(),
    fieldKey: f.fieldKey,
    fieldGroup: f.fieldGroup,
    displayLabel: f.displayLabel,
    isIncluded: f.on ?? true,
    isDefaultIncluded: f.on ?? true,
  }));

// ── Utility Bill Verification (Electricity / PNG / LPG) ──────────────────────────
// One step, three connection-type methods. Like ID Verification, Risk Rules and
// Return Data are configured PER connection type — each ruleKey / fieldKey is
// namespaced "<type>::" so the engine filters to whichever type the applicant ran.
const UTILITY_RISK_RULES: Array<{
  ruleKey: string;
  action: string;
  config: string | null;
  isEnabled?: boolean;
}> = [
  // Electricity
  { ruleKey: 'electricity::bill_recency_restriction', action: 'Review', config: '3' },
  { ruleKey: 'electricity::outstanding_bill_overdue', action: 'Review', config: null },
  { ruleKey: 'electricity::missing_address', action: 'Reject', config: null },
  { ruleKey: 'electricity::duplicate_consumer_number', action: 'Reject', config: null },
  // PNG
  { ruleKey: 'png::bill_recency_restriction', action: 'Review', config: '3' },
  { ruleKey: 'png::bill_due_date_elapsed', action: 'Review', config: null },
  { ruleKey: 'png::missing_address', action: 'Reject', config: null },
  { ruleKey: 'png::duplicate_bill_number', action: 'Reject', config: null },
  // LPG
  { ruleKey: 'lpg::consumer_status_not_active', action: 'Reject', config: null },
  { ruleKey: 'lpg::booking_recency_restriction', action: 'Review', config: '6' },
  { ruleKey: 'lpg::duplicate_consumer_number', action: 'Reject', config: null },
  { ruleKey: 'lpg::aadhaar_not_linked', action: 'Review', config: null },
];

const UTILITY_RETURN_FIELDS: Array<{
  fieldKey: string;
  fieldGroup: string;
  displayLabel: string;
  on?: boolean;
}> = [
  // ── Electricity (PDF: Electricity Connection · Return Data) ──
  {
    fieldKey: 'electricity::bill_no',
    fieldGroup: 'Electricity · Identity & Bill Details',
    displayLabel: 'Bill Number',
  },
  {
    fieldKey: 'electricity::consumer_number',
    fieldGroup: 'Electricity · Identity & Bill Details',
    displayLabel: 'Consumer Number',
  },
  {
    fieldKey: 'electricity::bill_issue_date',
    fieldGroup: 'Electricity · Identity & Bill Details',
    displayLabel: 'Bill Issue Date',
  },
  {
    fieldKey: 'electricity::bill_due_date',
    fieldGroup: 'Electricity · Identity & Bill Details',
    displayLabel: 'Bill Due Date',
  },
  {
    fieldKey: 'electricity::consumer_name',
    fieldGroup: 'Electricity · Customer Information',
    displayLabel: 'Consumer Name',
  },
  {
    fieldKey: 'electricity::address',
    fieldGroup: 'Electricity · Customer Information',
    displayLabel: 'Address',
  },
  {
    fieldKey: 'electricity::mobile_number',
    fieldGroup: 'Electricity · Customer Information',
    displayLabel: 'Mobile Number',
  },
  {
    fieldKey: 'electricity::email_address',
    fieldGroup: 'Electricity · Customer Information',
    displayLabel: 'Email Address',
  },
  {
    fieldKey: 'electricity::bill_amount',
    fieldGroup: 'Electricity · Financial Details',
    displayLabel: 'Bill Amount',
  },
  {
    fieldKey: 'electricity::amount_payable',
    fieldGroup: 'Electricity · Financial Details',
    displayLabel: 'Amount Payable',
  },
  {
    fieldKey: 'electricity::total_amount',
    fieldGroup: 'Electricity · Financial Details',
    displayLabel: 'Total Amount',
  },
  {
    fieldKey: 'electricity::duplicate_consumer_flag',
    fieldGroup: 'Electricity · Risk Signals',
    displayLabel: 'Duplicate Consumer Flag',
  },
  {
    fieldKey: 'electricity::bill_overdue_flag',
    fieldGroup: 'Electricity · Risk Signals',
    displayLabel: 'Bill Overdue Flag',
  },
  {
    fieldKey: 'electricity::verification_status',
    fieldGroup: 'Electricity · Metadata',
    displayLabel: 'Status',
  },
  {
    fieldKey: 'electricity::verified_at',
    fieldGroup: 'Electricity · Metadata',
    displayLabel: 'Verified At',
  },
  {
    fieldKey: 'electricity::method_used',
    fieldGroup: 'Electricity · Metadata',
    displayLabel: 'Method Used',
  },
  {
    fieldKey: 'electricity::verification_attempts',
    fieldGroup: 'Electricity · Metadata',
    displayLabel: 'Verification Attempts',
  },
  // ── PNG (PDF: PNG Connection · Return Data) ──
  {
    fieldKey: 'png::bill_no',
    fieldGroup: 'PNG · Identity & Bill Details',
    displayLabel: 'Bill Number',
  },
  {
    fieldKey: 'png::bill_date',
    fieldGroup: 'PNG · Identity & Bill Details',
    displayLabel: 'Bill Date',
  },
  {
    fieldKey: 'png::due_date',
    fieldGroup: 'PNG · Identity & Bill Details',
    displayLabel: 'Due Date',
  },
  {
    fieldKey: 'png::customer_name',
    fieldGroup: 'PNG · Customer Information',
    displayLabel: 'Customer Name',
  },
  {
    fieldKey: 'png::customer_address',
    fieldGroup: 'PNG · Customer Information',
    displayLabel: 'Customer Address',
  },
  {
    fieldKey: 'png::mobile',
    fieldGroup: 'PNG · Customer Information',
    displayLabel: 'Mobile Number',
  },
  {
    fieldKey: 'png::email',
    fieldGroup: 'PNG · Customer Information',
    displayLabel: 'Email Address',
  },
  {
    fieldKey: 'png::bill_amount',
    fieldGroup: 'PNG · Financial Details',
    displayLabel: 'Bill Amount',
  },
  {
    fieldKey: 'png::duplicate_bill_flag',
    fieldGroup: 'PNG · Risk Signals',
    displayLabel: 'Duplicate Bill Flag',
  },
  {
    fieldKey: 'png::due_date_elapsed_flag',
    fieldGroup: 'PNG · Risk Signals',
    displayLabel: 'Due Date Elapsed Flag',
  },
  { fieldKey: 'png::verification_status', fieldGroup: 'PNG · Metadata', displayLabel: 'Status' },
  { fieldKey: 'png::verified_at', fieldGroup: 'PNG · Metadata', displayLabel: 'Verified At' },
  { fieldKey: 'png::method_used', fieldGroup: 'PNG · Metadata', displayLabel: 'Method Used' },
  {
    fieldKey: 'png::verification_attempts',
    fieldGroup: 'PNG · Metadata',
    displayLabel: 'Verification Attempts',
  },
  // ── LPG (PDF: LPG Connection · Return Data) ──
  {
    fieldKey: 'lpg::consumer_no',
    fieldGroup: 'LPG · Identity Details',
    displayLabel: 'Consumer Number',
  },
  {
    fieldKey: 'lpg::distributor_name',
    fieldGroup: 'LPG · Identity Details',
    displayLabel: 'Distributor Name',
  },
  {
    fieldKey: 'lpg::consumer_name',
    fieldGroup: 'LPG · Customer Information',
    displayLabel: 'Consumer Name',
  },
  {
    fieldKey: 'lpg::consumer_address',
    fieldGroup: 'LPG · Customer Information',
    displayLabel: 'Consumer Address',
  },
  {
    fieldKey: 'lpg::consumer_contact',
    fieldGroup: 'LPG · Customer Information',
    displayLabel: 'Consumer Contact',
  },
  {
    fieldKey: 'lpg::consumer_email',
    fieldGroup: 'LPG · Customer Information',
    displayLabel: 'Consumer Email',
  },
  {
    fieldKey: 'lpg::city_town',
    fieldGroup: 'LPG · Customer Information',
    displayLabel: 'City / Town',
  },
  { fieldKey: 'lpg::pin', fieldGroup: 'LPG · Customer Information', displayLabel: 'PIN Code' },
  {
    fieldKey: 'lpg::aadhaar_no',
    fieldGroup: 'LPG · Subsidy & Banking Details',
    displayLabel: 'Aadhaar Number',
  },
  {
    fieldKey: 'lpg::bank_name',
    fieldGroup: 'LPG · Subsidy & Banking Details',
    displayLabel: 'Bank Name',
  },
  {
    fieldKey: 'lpg::bank_account_no',
    fieldGroup: 'LPG · Subsidy & Banking Details',
    displayLabel: 'Bank Account Number',
  },
  {
    fieldKey: 'lpg::ifsc_code',
    fieldGroup: 'LPG · Subsidy & Banking Details',
    displayLabel: 'IFSC Code',
  },
  {
    fieldKey: 'lpg::approximate_subsidy_availed',
    fieldGroup: 'LPG · Subsidy & Banking Details',
    displayLabel: 'Approximate Subsidy Availed',
    on: false,
  },
  {
    fieldKey: 'lpg::subsidized_refill_consumed',
    fieldGroup: 'LPG · Subsidy & Banking Details',
    displayLabel: 'Subsidised Refills Consumed',
    on: false,
  },
  {
    fieldKey: 'lpg::given_up_subsidy',
    fieldGroup: 'LPG · Subsidy & Banking Details',
    displayLabel: 'Given-Up Subsidy',
    on: false,
  },
  {
    fieldKey: 'lpg::status',
    fieldGroup: 'LPG · Consumption & Activity Details',
    displayLabel: 'Connection Status',
  },
  {
    fieldKey: 'lpg::total_refill_consumed',
    fieldGroup: 'LPG · Consumption & Activity Details',
    displayLabel: 'Total Refills Consumed',
  },
  {
    fieldKey: 'lpg::last_booking_date',
    fieldGroup: 'LPG · Consumption & Activity Details',
    displayLabel: 'Last Booking Date',
  },
  {
    fieldKey: 'lpg::name_match_score',
    fieldGroup: 'LPG · Risk Signals',
    displayLabel: 'Name Match Score',
  },
  {
    fieldKey: 'lpg::duplicate_consumer_flag',
    fieldGroup: 'LPG · Risk Signals',
    displayLabel: 'Duplicate Consumer Flag',
  },
  {
    fieldKey: 'lpg::dormant_connection_flag',
    fieldGroup: 'LPG · Risk Signals',
    displayLabel: 'Dormant Connection Flag',
  },
  {
    fieldKey: 'lpg::aadhaar_not_linked_flag',
    fieldGroup: 'LPG · Risk Signals',
    displayLabel: 'Aadhaar Not Linked Flag',
  },
  { fieldKey: 'lpg::verification_status', fieldGroup: 'LPG · Metadata', displayLabel: 'Status' },
  { fieldKey: 'lpg::verified_at', fieldGroup: 'LPG · Metadata', displayLabel: 'Verified At' },
  { fieldKey: 'lpg::method_used', fieldGroup: 'LPG · Metadata', displayLabel: 'Method Used' },
  {
    fieldKey: 'lpg::verification_attempts',
    fieldGroup: 'LPG · Metadata',
    displayLabel: 'Verification Attempts',
  },
];

const createDefaultUtilityRiskRules = () =>
  UTILITY_RISK_RULES.map((r) => ({
    id: crypto.randomUUID(),
    ruleKey: r.ruleKey,
    isEnabled: r.isEnabled ?? true,
    action: r.action,
    config: r.config,
  }));

const createDefaultUtilityReturnFields = () =>
  UTILITY_RETURN_FIELDS.map((f) => ({
    id: crypto.randomUUID(),
    fieldKey: f.fieldKey,
    fieldGroup: f.fieldGroup,
    displayLabel: f.displayLabel,
    isIncluded: f.on ?? true,
    isDefaultIncluded: f.on ?? true,
  }));

// Default Ration Card Risk Rules:
//   Duplicate UID Detected   → Review   when the card flags a duplicate UID
//   Missing Address          → Review   when the card carries no address
//   Name Mismatch (vs PAN/Aadhaar) → Review when head-of-family name score < 70
//   Duplicate Ration Card    → Reject   when the same card was verified earlier
//                              (Studio cross-session ledger)
const createDefaultRationRiskRules = () => [
  {
    id: crypto.randomUUID(),
    ruleKey: 'duplicate_uid_detected',
    isEnabled: true,
    action: 'Review',
    config: null,
  },
  {
    id: crypto.randomUUID(),
    ruleKey: 'missing_ration_address',
    isEnabled: true,
    action: 'Review',
    config: null,
  },
  {
    id: crypto.randomUUID(),
    ruleKey: 'name_match_score',
    isEnabled: true,
    action: 'Review',
    config: '70',
  },
  {
    id: crypto.randomUUID(),
    ruleKey: 'duplicate_ration_card',
    isEnabled: true,
    action: 'Reject',
    config: null,
  },
];

// Default Ration Card Return Data — mapped to the real gateway response (Protean
// /api/v1/ration/details: rcId / state / district / fpsId / schemeName /
// dupUidStatus / allowdOnorc / address / memberDetails[]). member_count / members /
// head_of_family are derived from memberDetails; name_match_score /
// duplicate_ration_card_flag are Studio-computed.
const createDefaultRationReturnFields = () => [
  {
    id: crypto.randomUUID(),
    fieldKey: 'ration_card_number',
    fieldGroup: 'Identity Details',
    displayLabel: 'Ration Card Number',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'state',
    fieldGroup: 'Identity Details',
    displayLabel: 'State',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'district',
    fieldGroup: 'Identity Details',
    displayLabel: 'District',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'fps_id',
    fieldGroup: 'Card Details',
    displayLabel: 'Fair Price Shop ID',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'scheme_name',
    fieldGroup: 'Card Details',
    displayLabel: 'Scheme Name',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'onorc_allowed',
    fieldGroup: 'Card Details',
    displayLabel: 'ONORC Allowed',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'address',
    fieldGroup: 'Card Details',
    displayLabel: 'Address',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'head_of_family',
    fieldGroup: 'Household Details',
    displayLabel: 'Head of Family',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'member_count',
    fieldGroup: 'Household Details',
    displayLabel: 'Number of Members',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'members',
    fieldGroup: 'Household Details',
    displayLabel: 'Members',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'duplicate_uid_status',
    fieldGroup: 'Risk Signals',
    displayLabel: 'Duplicate UID Status',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'name_match_score',
    fieldGroup: 'Risk Signals',
    displayLabel: 'Name Match Score',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'name_matched_against',
    fieldGroup: 'Risk Signals',
    displayLabel: 'Name Matched Against',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'duplicate_ration_card_flag',
    fieldGroup: 'Risk Signals',
    displayLabel: 'Duplicate Ration Card Flag',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'verification_status',
    fieldGroup: 'Metadata',
    displayLabel: 'Status',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'verification_attempts',
    fieldGroup: 'Metadata',
    displayLabel: 'Verification Attempts',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'verified_at',
    fieldGroup: 'Metadata',
    displayLabel: 'Verified At',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'method_used',
    fieldGroup: 'Metadata',
    displayLabel: 'Method Used',
    isIncluded: true,
    isDefaultIncluded: true,
  },
  {
    id: crypto.randomUUID(),
    fieldKey: 'session_id',
    fieldGroup: 'Metadata',
    displayLabel: 'Session ID',
    isIncluded: true,
    isDefaultIncluded: true,
  },
];

// Default MSME (Udyam) Risk Rules (SEBI spec):
//   Enterprise Type Restriction → Review  typeOfEnterprise not in allowed list
//                                          (config = comma-separated allowed types)
//   PAN Mismatch                → Reject  profile.pan ≠ applicant PAN on record
//   Turnover Threshold Breach   → Review  net turnover > configured limit
//   Recent Incorporation        → Review  months since incorporation < min vintage
//   Duplicate Udyam             → Reject  same Udyam number verified earlier (ledger)
const createDefaultMsmeRiskRules = () => [
  {
    id: crypto.randomUUID(),
    ruleKey: 'enterprise_type_restriction',
    isEnabled: true,
    action: 'Review',
    // Stored in a jsonb column → MUST be valid JSON (a bare "Micro,Small" is not).
    config: '["Micro","Small"]',
  },
  {
    id: crypto.randomUUID(),
    ruleKey: 'pan_mismatch',
    isEnabled: true,
    action: 'Reject',
    config: null,
  },
  {
    id: crypto.randomUUID(),
    ruleKey: 'turnover_threshold_breach',
    isEnabled: true,
    action: 'Review',
    config: '500000000',
  },
  {
    id: crypto.randomUUID(),
    ruleKey: 'recent_incorporation',
    isEnabled: true,
    action: 'Review',
    config: '6',
  },
  {
    id: crypto.randomUUID(),
    ruleKey: 'duplicate_udyam',
    isEnabled: true,
    action: 'Reject',
    config: null,
  },
];

// Default MSME (Udyam) Return Data — mapped 1:1 to the real gateway response
// (Udyam without OTP, /api/v1/udyamverification: udyamRegistrationNo /
// dateOfRegistration / typeOfEnterprise / enterpriseType[] / industry[] /
// interestedIn* / profile{…} / bankDetails{…} / employmentDetails{…} /
// financials.details{…} / officialAddress / branchDetails[]). The Risk-Signal flags
// and Metadata are Studio-computed. bank_account_no + branch_details default OFF
// (sensitive / verbose per the spec).
const createDefaultMsmeReturnFields = () => {
  const spec: Array<[string, string, string, boolean]> = [
    // [fieldKey, fieldGroup, displayLabel, defaultIncluded]
    ['udyam_registration_no', 'Identity & Classification', 'Udyam Registration Number', true],
    ['date_of_registration', 'Identity & Classification', 'Date of Registration', true],
    ['type_of_enterprise', 'Identity & Classification', 'Type of Enterprise', true],
    ['enterprise_type_history', 'Identity & Classification', 'Enterprise Type History', true],
    ['industry_classification', 'Identity & Classification', 'Industry Classification', true],
    ['interested_in_treds', 'Identity & Classification', 'Interested in TReDS', true],
    ['interested_in_gem', 'Identity & Classification', 'Interested in GeM', true],
    ['business_name', 'Enterprise Profile', 'Business Name', true],
    ['major_activity', 'Enterprise Profile', 'Major Activity', true],
    ['organization_type', 'Enterprise Profile', 'Organization Type', true],
    ['owner_name', 'Enterprise Profile', 'Owner Name', true],
    ['pan', 'Enterprise Profile', 'PAN', true],
    ['gender', 'Enterprise Profile', 'Gender', true],
    ['social_category', 'Enterprise Profile', 'Social Category', true],
    ['specially_abled', 'Enterprise Profile', 'Specially Abled', true],
    ['date_of_incorporation', 'Enterprise Profile', 'Date of Incorporation', true],
    ['date_of_commencement', 'Enterprise Profile', 'Date of Commencement', true],
    ['previous_itr_status', 'Compliance & Contact', 'Previous ITR Status', true],
    ['itr_type', 'Compliance & Contact', 'ITR Type', true],
    ['gstn_status', 'Compliance & Contact', 'GSTIN Status', true],
    ['mobile', 'Compliance & Contact', 'Mobile Number', true],
    ['email_id', 'Compliance & Contact', 'Email Address', true],
    ['bank_name', 'Bank Details', 'Bank Name', true],
    ['ifsc_code', 'Bank Details', 'IFSC Code', true],
    ['bank_account_no', 'Bank Details', 'Bank Account Number', false],
    ['employment_male', 'Employment & Financial', 'Male Employees', true],
    ['employment_female', 'Employment & Financial', 'Female Employees', true],
    ['employment_others', 'Employment & Financial', 'Other Employees', true],
    ['employment_total', 'Employment & Financial', 'Total Employees', true],
    ['financial_year', 'Employment & Financial', 'Financial Year', true],
    ['wdv_pme', 'Employment & Financial', 'WDV of Plant & Machinery', true],
    ['exclusion', 'Employment & Financial', 'Exclusion', true],
    ['net_investment_in_pme', 'Employment & Financial', 'Net Investment in P&M', true],
    ['total_turnover', 'Employment & Financial', 'Total Turnover', true],
    ['export_turnover', 'Employment & Financial', 'Export Turnover', true],
    ['net_turnover', 'Employment & Financial', 'Net Turnover', true],
    ['official_address', 'Address & Location', 'Official Address', true],
    ['branch_details', 'Address & Location', 'Branch Details', false],
    ['pdf_document', 'Document', 'Registration Certificate (PDF)', true],
    ['enterprise_type_mismatch_flag', 'Risk Signals', 'Enterprise Type Mismatch Flag', true],
    ['pan_match_flag', 'Risk Signals', 'PAN Match Flag', true],
    ['turnover_threshold_flag', 'Risk Signals', 'Turnover Threshold Flag', true],
    ['duplicate_udyam_flag', 'Risk Signals', 'Duplicate Udyam Flag', true],
    ['verification_status', 'Metadata', 'Status', true],
    ['verification_attempts', 'Metadata', 'Verification Attempts', true],
    ['verified_at', 'Metadata', 'Verified At', true],
    ['method_used', 'Metadata', 'Method Used', true],
    ['session_id', 'Metadata', 'Session ID', true],
  ];
  return spec.map(([fieldKey, fieldGroup, displayLabel, isDefaultIncluded]) => ({
    id: crypto.randomUUID(),
    fieldKey,
    fieldGroup,
    displayLabel,
    isIncluded: isDefaultIncluded,
    isDefaultIncluded,
  }));
};

// One place mapping a step type to its SEBI default Risk Rules / Return Data —
// used by the new-step path, the blank-workflow bootstrap and the backfill.
function defaultRiskRulesFor(stepType: string) {
  switch (stepType) {
    case 'pan_verification':
      return createDefaultPanRiskRules();
    case 'aadhaar_otp':
      return createDefaultAadhaarRiskRules();
    case 'face_match':
      return createDefaultFaceRiskRules();
    case 'liveness':
      return createDefaultLivenessRiskRules();
    case 'bank_account':
      return createDefaultBankRiskRules();
    case 'gstin':
      return createDefaultGstRiskRules();
    case 'id_verification':
      return createDefaultIdRiskRules();
    case 'utility_bill':
      return createDefaultUtilityRiskRules();
    case 'ration_card':
      return createDefaultRationRiskRules();
    case 'msme':
      return createDefaultMsmeRiskRules();
    default:
      return [];
  }
}

function defaultReturnFieldsFor(stepType: string) {
  switch (stepType) {
    case 'pan_verification':
      return createDefaultPanReturnFields();
    case 'aadhaar_otp':
      return createDefaultAadhaarReturnFields();
    case 'face_match':
      return createDefaultFaceReturnFields();
    case 'liveness':
      return createDefaultLivenessReturnFields();
    case 'bank_account':
      return createDefaultBankReturnFields();
    case 'gstin':
      return createDefaultGstReturnFields();
    case 'id_verification':
      return createDefaultIdReturnFields();
    case 'utility_bill':
      return createDefaultUtilityReturnFields();
    case 'ration_card':
      return createDefaultRationReturnFields();
    case 'msme':
      return createDefaultMsmeReturnFields();
    default:
      return [];
  }
}

// Backfills the SEBI default Risk Rules AND Return Data onto any loaded step that
// has none yet (e.g. an Aadhaar step created before these were wired, or an older
// PAN step). This mutates the real step data — NOT a display trick — so the rows
// become genuine and are persisted to the DB on the next save. Returns the
// possibly-seeded steps plus whether anything was seeded (so the caller can mark
// the form dirty). Each field is backfilled independently — a step missing only
// its return fields keeps its existing risk rules, and vice-versa.
function backfillStepDefaults(steps: WorkflowStepDto[]): {
  steps: WorkflowStepDto[];
  seeded: boolean;
} {
  let seeded = false;
  const next = steps.map((step) => {
    let patched = step;

    // Seed default Settings onto steps saved with none (older steps whose
    // Settings tab was never touched persisted as null → no expiry/lock ever
    // enforced). Materialise the defaults the panel already displays so they
    // become real, saveable values.
    if (step.settings === null || step.settings === undefined || step.settings === '') {
      patched = { ...patched, settings: DEFAULT_STEP_SETTINGS_JSON };
      seeded = true;
    }

    if (step.riskRules.length === 0) {
      const rules = defaultRiskRulesFor(step.stepType);
      if (rules.length > 0) {
        patched = { ...patched, riskRules: rules };
        seeded = true;
      }
    }

    // Seed when empty, OR repair an Aadhaar step that still carries the earlier
    // mismatched demographic keys (aadhaar_name/date_of_birth/pin_code/…) — those
    // don't match the real Secure QR response, so conditions/filters on them
    // silently no-op. Safe to replace: these are machine-seeded defaults, never
    // hand-authored. Detect by the tell-tale old key `aadhaar_name`.
    const hasStaleAadhaarKeys =
      step.stepType === 'aadhaar_otp' &&
      step.returnFields.some((f) => f.fieldKey === 'aadhaar_name');
    // Upgrade older PAN steps seeded with the earlier 6-field set to the full
    // SEBI §4.5 set (adds Risk Signals + Metadata). Detect by any canonical key
    // being absent. Machine-seeded defaults, safe to re-seed.
    const hasIncompletePanReturnFields =
      step.stepType === 'pan_verification' &&
      step.returnFields.length > 0 &&
      PAN_RETURN_FIELD_KEYS.some((key) => !step.returnFields.some((f) => f.fieldKey === key));
    // Heal steps seeded with the now-removed `node_id` field (dropped per spec).
    const hasRemovedNodeId = step.returnFields.some((f) => f.fieldKey === 'node_id');
    if (
      step.returnFields.length === 0 ||
      hasStaleAadhaarKeys ||
      hasIncompletePanReturnFields ||
      hasRemovedNodeId
    ) {
      const fields = defaultReturnFieldsFor(step.stepType);
      if (fields.length > 0) {
        patched = { ...patched, returnFields: fields };
        seeded = true;
      }
    }

    return patched;
  });
  return { steps: next, seeded };
}

// ── Main screen ───────────────────────────────────────────────────────────────

/**
 * The display name a Questionnaire step should carry — the questionnaire it asks.
 *
 * Returns null for every other step type, and for a questionnaire step with nothing attached, so the
 * caller keeps whatever name the step already had.
 */
function questionnaireDisplayName(step: WorkflowStepDto): string | null {
  if (step.stepType !== 'questionnaire') return null;
  const { questionnaireName } = parseQuestionnaireStepSettings(step.settings);
  return questionnaireName?.trim() || null;
}

export function SimpleWorkflowEditorScreen() {
  const { workflowId } = useParams<{ workflowId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  // Read-only "View" mode (opened from the list's View action, ?mode=view).
  // Disables every control and hides Save so the workflow can be inspected
  // without being edited.
  const isView = searchParams.get('mode') === 'view';
  const { data: workflow, isLoading, isError } = useWorkflow(workflowId);
  const saveWorkflow = useSaveWorkflowFull();
  const publishWorkflow = usePublishWorkflow();
  // Workflows offered by the header's "Change Workflow" dropdown. One page is
  // plenty for a switcher; the full list lives on /workflows.
  const { data: workflowList } = useListWorkflows({ page: 1, pageSize: 50 });
  const switchableWorkflows = workflowList?.items ?? [];
  const deleteWorkflow = usePermanentDeleteWorkflow();
  const copyLink = useCopyWorkflowLink();
  const navigate = useNavigate();

  const [steps, setSteps] = useState<WorkflowStepDto[]>([]);
  const [selectedStepIdx, setSelectedStepIdx] = useState<number | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  // Inline rename, the designer's pencil affordance. Also reveals the
  // description input, which the design has no separate slot for.
  const [isEditingName, setIsEditingName] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [customizationOpen, setCustomizationOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [workflowSettings, setWorkflowSettings] =
    useState<WorkflowSettings>(DEFAULT_WORKFLOW_SETTINGS);
  const [workflowName, setWorkflowName] = useState('');
  const [workflowDescription, setWorkflowDescription] = useState('');
  // Phone-preview screen. 0 is always the welcome overview; then one screen per
  // enabled API of the selected step, then the green success screen. Method-tab
  // toggles jump the preview to the enabled API's screen (designer behaviour).
  const [previewScreen, setPreviewScreen] = useState(0);

  // The builder is a three-column, full-viewport screen (steps · config · phone
  // preview), so it opens with the app sidebar collapsed to give those columns
  // room — matching the design, which renders this page collapsed.
  //
  // sidebarCollapsed is a GLOBAL, persisted preference, so this restores whatever
  // the user had on the way out. Collapsing here must not silently rewrite their
  // choice for every other screen. Expanding it while in the builder still works;
  // that manual change is simply not carried out of the page.
  const setSidebarCollapsed = useUiStore((s) => s.setSidebarCollapsed);
  useEffect(() => {
    const previous = useUiStore.getState().sidebarCollapsed;
    setSidebarCollapsed(true);
    return () => setSidebarCollapsed(previous);
  }, [setSidebarCollapsed]);

  // Tracks which workflowId received the initial load so we only auto-populate once
  // per navigation — not on every re-fetch after save.
  const initializedWorkflowId = useRef<string | null>(null);

  useEffect(() => {
    if (!workflow) return;

    const loaded = workflow.steps ?? [];
    const isFirstLoad = initializedWorkflowId.current !== workflow.id;

    if (isFirstLoad) {
      initializedWorkflowId.current = workflow.id;

      if (loaded.length === 0) {
        // New / blank workflow — auto-add PAN Verification as the starting step
        // and select it so the user lands on its settings immediately.
        const meta = STEP_TYPE_META.pan_verification;
        const panStep: WorkflowStepDto = {
          id: crypto.randomUUID(),
          stepType: 'pan_verification',
          displayName: meta?.label ?? 'PAN Verification',
          position: 0,
          isMandatory: false,
          isEnabled: true,
          settings: DEFAULT_STEP_SETTINGS_JSON,
          apiBindings: [],
          riskRules: createDefaultPanRiskRules(),
          conditions: [],
          returnFields: createDefaultPanReturnFields(),
        };
        setSteps([panStep]);
        setSelectedStepIdx(0);
        setIsDirty(true);
      } else {
        // Existing workflow — backfill default risk rules + return data onto any
        // step that has none (older Aadhaar/PAN steps), so they become real,
        // saveable data. Mark dirty when we seeded so a plain Save persists to DB.
        const { steps: seededSteps, seeded } = backfillStepDefaults(loaded);
        setSteps(seededSteps);
        setSelectedStepIdx(0);
        if (seeded) setIsDirty(true);
      }
    } else {
      // Re-fetch after save — sync steps but leave selection intact
      setSteps(loaded);
    }

    setWorkflowName(workflow.name);
    setWorkflowDescription(workflow.description ?? '');
    setWorkflowSettings(parseWorkflowSettings(workflow.settingsJson));
    if (!isFirstLoad) setIsDirty(false);
  }, [workflow]);

  // Returning from the template designer with ?attachTemplate=<id>. The designer
  // cannot write the workflow itself — it doesn't own the step list — so it hands the
  // id back through the URL and the link is made here, once, then the param is
  // dropped so a refresh doesn't reapply it.
  useEffect(() => {
    const attachTemplateId = searchParams.get('attachTemplate');
    if (!attachTemplateId || steps.length === 0) return;

    // WHICH template step — a workflow can hold several. The designer echoes back the
    // step it was opened from; falling back to "the first one" would attach the
    // document to the wrong step as soon as there are two.
    const forStepId = searchParams.get('forStep');
    const index = forStepId
      ? steps.findIndex((s) => s.id === forStepId)
      : steps.findIndex((s) => s.stepType === 'document_template');
    if (index === -1) return;

    const current = steps[index];
    if (!current) return;

    const existing = parseTemplateStepSettings(current.settings);
    if (existing.templateId !== attachTemplateId) {
      const settings = writeTemplateStepSettings(current.settings, {
        templateId: attachTemplateId,
      });
      setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, settings } : s)));
      setIsDirty(true);
      setSelectedStepIdx(index);
    }

    const next = new URLSearchParams(searchParams);
    next.delete('attachTemplate');
    next.delete('forStep');
    setSearchParams(next, { replace: true });
  }, [searchParams, steps, setSearchParams]);

  useEffect(() => {
    function handleBeforeUnload(e: BeforeUnloadEvent) {
      if (isDirty) e.preventDefault();
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  function handleAddStep(stepType: string) {
    // Guard unmet step dependencies: Face Match needs an earlier step whose
    // ENABLED APIs return a reference photo (Aadhaar DigiLocker/Secure QR, or ID
    // Verification Driving Licence). Clicking without one shows this toast.
    const blocked = stepDependencyReason(
      stepType,
      workflowHasFaceMatchPhotoSource(steps, catalogue),
    );
    if (blocked) {
      toast.error(blocked);
      return;
    }

    const meta = STEP_TYPE_META[stepType];
    const newStep: WorkflowStepDto = {
      id: crypto.randomUUID(),
      stepType,
      displayName: meta?.label ?? stepType,
      position: steps.length,
      isMandatory: false,
      isEnabled: true,
      settings: DEFAULT_STEP_SETTINGS_JSON,
      apiBindings: [],
      riskRules: defaultRiskRulesFor(stepType),
      conditions: [],
      returnFields: defaultReturnFieldsFor(stepType),
    };
    setSteps((prev) => [...prev, newStep]);
    setIsDirty(true);
  }

  // After removing a step, a Face Match step may be left with no photo source
  // (Aadhaar DigiLocker/Secure QR, or ID Verification Driving Licence). Drop that
  // orphaned Face Match too and tell the admin. Guards on the catalogue being
  // loaded so Face Match is never removed just because the catalogue is still
  // resolving.
  function dropOrphanedFaceMatch(next: WorkflowStepDto[]): WorkflowStepDto[] {
    if (!next.some((s) => s.stepType === 'face_match')) return next;
    if (!catalogue || catalogue.length === 0) return next;
    if (workflowHasFaceMatchPhotoSource(next, catalogue)) return next;
    toast.info(
      'Face Match was removed too — it needs an Aadhaar (DigiLocker / Secure QR) or Driving Licence photo source.',
    );
    return next.filter((s) => s.stepType !== 'face_match');
  }

  // Apply a step removal: cascade-drop an orphaned Face Match and keep the
  // selection on the same step (by id), clearing it when that step is gone.
  //
  // Returns false when the removal was REFUSED because it would empty the workflow.
  // A workflow with no steps verifies nothing, and the backend already rejects
  // publishing one (Errors.Workflow.CannotPublishWithNoSteps) — so without this the
  // admin could strip a draft to nothing, have it save happily, and only discover the
  // problem at Publish. Enforced HERE, on the resulting list, rather than on
  // `steps.length === 1` at the call sites: the Face Match cascade can remove a
  // SECOND step, so deleting one of two steps (Aadhaar + Face Match) would otherwise
  // still land on empty.
  function applyStepRemoval(next: WorkflowStepDto[]): boolean {
    const selectedId = selectedStepIdx !== null ? steps[selectedStepIdx]?.id : null;
    const cascaded = dropOrphanedFaceMatch(next);
    if (cascaded.length === 0) {
      toast.warning('A workflow needs at least one verification step.');
      return false;
    }
    setSteps(cascaded);
    const newIdx = selectedId ? cascaded.findIndex((s) => s.id === selectedId) : -1;
    setSelectedStepIdx(newIdx >= 0 ? newIdx : null);
    setIsDirty(true);
    return true;
  }

  // Removing drops the step's APIs, risk rules and settings with it, so snapshot
  // the list first and offer it back rather than making the admin rebuild by hand.
  function removeStepAt(idx: number) {
    const removed = steps[idx];
    if (!removed) return;
    const previousSteps = steps;
    const previousSelectedIdx = selectedStepIdx;
    // Also cascade-removes an orphaned Face Match (see applyStepRemoval). A refused
    // removal must not show the "removed / Undo" toast — nothing changed.
    if (!applyStepRemoval(steps.filter((_, i) => i !== idx))) return;
    toast(`${STEP_TYPE_META[removed.stepType]?.label ?? 'Step'} removed`, {
      action: {
        label: 'Undo',
        onClick: () => {
          setSteps(previousSteps);
          setSelectedStepIdx(previousSelectedIdx);
          setIsDirty(true);
        },
      },
    });
  }

  function handleDeleteStep() {
    if (selectedStepIdx === null) return;
    removeStepAt(selectedStepIdx);
  }

  // The ✕ on a catalog row's marker. Routed through removeStepAt so it shares the
  // orphaned-Face-Match cascade, the selection fix-up and the Undo toast with the
  // settings panel's Delete — one removal path, not two.
  //
  // Keyed by step ID rather than step type: a workflow can hold several steps of the
  // same type (two Document Templates, say), and a type lookup would always remove the
  // first of them however far down the list the ✕ was clicked.
  function handleRemoveStep(stepId: string) {
    const idx = steps.findIndex((s) => s.id === stepId);
    if (idx >= 0) removeStepAt(idx);
  }

  // Drag-reorder in the catalog: rebuild the steps array in the new position order.
  // Keyed by step ID, not step type: a workflow can hold several Document Template
  // steps, and a type-keyed map would collapse them onto one another.
  function handleReorderSteps(orderedIds: string[]) {
    setSteps((prev) => {
      const byId = new Map(prev.map((s) => [s.id, s]));
      return orderedIds
        .map((id, i) => {
          const s = byId.get(id);
          return s ? { ...s, position: i } : null;
        })
        .filter((s): s is WorkflowStepDto => s !== null);
    });
    setIsDirty(true);
  }

  /** An existing row was clicked — open that step. */
  function handleSelectStep(stepId: string) {
    const idx = steps.findIndex((s) => s.id === stepId);
    if (idx >= 0) setSelectedStepIdx(idx);
  }

  /**
   * A picker tile was clicked: add a step of this type, or select the one that is
   * already there. Every step type is once-only, so a workflow never holds two.
   */
  function handleAddStepType(stepType: string) {
    // A REPEATABLE type always adds. For every other type, clicking a tile whose step is already in
    // the workflow selects that step — the tile doubles as a shortcut to it. Applied to a repeatable
    // type that rule made the second click look like a dead button: it silently re-selected the first
    // Questionnaire instead of adding a second one.
    const idx = isRepeatableStepType(stepType)
      ? -1
      : steps.findIndex((s) => s.stepType === stepType);

    if (idx >= 0) {
      setSelectedStepIdx(idx);
    } else {
      // Don't select a step that couldn't be added (unmet dependency) — that
      // would point selectedStepIdx at a non-existent row.
      if (stepDependencyReason(stepType, workflowHasFaceMatchPhotoSource(steps, catalogue))) {
        handleAddStep(stepType); // surfaces the toast, adds nothing
        return;
      }
      handleAddStep(stepType);
      setSelectedStepIdx(steps.length); // new step is appended at the current length
    }
  }

  function handleSave() {
    if (!workflow || !workflowId) return;
    saveWorkflow.mutate(
      {
        workflowId,
        body: {
          name: workflowName,
          description: workflowDescription || null,
          language: workflow.language ?? 'en',
          settingsJson: JSON.stringify(workflowSettings),
          steps: steps.map((s, i) => ({
            id: s.id,
            stepType: s.stepType,
            // A Questionnaire step is NAMED BY THE QUESTIONNAIRE IT ASKS, derived here as well as on
            // attach. Attaching sets it, but steps built before that behaviour existed still carry the
            // generic "Questionnaire" — and that name is copied onto the session step at run start,
            // where it labels the applicant's welcome overview and the admin's step chips. Deriving it
            // at save means those older steps correct themselves the next time the workflow is saved,
            // instead of needing every one re-attached by hand.
            displayName: questionnaireDisplayName(s) ?? s.displayName,
            position: i,
            isMandatory: s.isMandatory,
            isEnabled: s.isEnabled,
            settings: s.settings,
            apiBindings: s.apiBindings.map((b) => ({
              id: b.id,
              catalogueEntryId: b.catalogueEntryId,
              isEnabled: b.isEnabled,
              priority: b.priority,
              inputMappings: b.inputMappings.map((m) => ({
                id: m.id,
                apiParam: m.apiParam,
                sourceType: m.sourceType,
                sourceKey: m.sourceKey,
              })),
            })),
            riskRules: s.riskRules.map((r) => ({
              id: r.id,
              ruleKey: r.ruleKey,
              isEnabled: r.isEnabled,
              action: r.action,
              config: r.config,
            })),
            // Skip not-yet-configured rule rows (blank Field/Value) — the
            // backend domain guard rejects empty conditions, so persisting
            // half-filled rows would 500 the save.
            conditions: s.conditions
              .filter((c) => c.ifField.trim() !== '' && c.ifValue.trim() !== '')
              .map((c, i) => ({
                id: c.id,
                sortOrder: i,
                ifField: c.ifField,
                operator: c.operator,
                ifValue: c.ifValue,
                thenAction: c.thenAction,
              })),
            returnFields: s.returnFields.map((f) => ({
              id: f.id,
              fieldKey: f.fieldKey,
              fieldGroup: f.fieldGroup,
              displayLabel: f.displayLabel,
              isIncluded: f.isIncluded,
              isDefaultIncluded: f.isDefaultIncluded,
            })),
          })),
        },
      },
      {
        onSuccess: () => {
          // No success toast — autosave runs often; the header "Saved"
          // indicator communicates status instead.
          setIsDirty(false);
        },
        onError: () => {
          toast.error('Failed to save workflow');
        },
      },
    );
  }

  // Keep the latest handleSave in a ref so the debounced autosave always calls
  // the current closure (fresh steps/name/description) without re-arming the
  // timer on every render.
  const handleSaveRef = useRef(handleSave);
  handleSaveRef.current = handleSave;

  // A rule row the user just added (via "Add Rule") starts blank. handleSave
  // strips blank conditions before sending (the backend rejects them), so
  // autosaving mid-entry would drop the row and the server re-sync would make
  // it vanish. We treat a workflow with any half-filled condition as
  // "not ready" and defer autosave until every rule has a Field and a Value
  // (or the blank row is removed).
  const hasIncompleteRules = steps.some((s) =>
    s.conditions.some((c) => c.ifField.trim() === '' || c.ifValue.trim() === ''),
  );

  // Autosave: persist ~800ms after the last edit. The timer re-arms whenever an
  // edit-tracked value changes, so a burst of edits debounces into one save.
  // Skipped in View mode, when nothing is pending, or while a rule is incomplete.
  // biome-ignore lint/correctness/useExhaustiveDependencies: steps/name/description are watched to reset the debounce; the save call goes through handleSaveRef.
  useEffect(() => {
    if (isView || !isDirty || hasIncompleteRules || !workflow || !workflowId) return;
    const timer = setTimeout(() => handleSaveRef.current(), 800);
    return () => clearTimeout(timer);
  }, [
    isDirty,
    isView,
    hasIncompleteRules,
    workflow,
    workflowId,
    steps,
    workflowName,
    workflowDescription,
  ]);

  // Real per-API prices from the seeded catalogue (credits/call), keyed by the
  // catalogue tag each step maps to. Same source the step picker uses, so the cost
  // footer totals exactly the prices shown in the list.
  const { data: catalogue } = useApiCatalogue();

  // The applicant's journey, resolved once for the whole phone preview: every
  // workflow step in order, with the FIRST-PRIORITY enabled API — the method the
  // applicant actually gets. Drives both the welcome overview (screen 0) and the
  // screens after it, so paging through the preview walks the real flow instead
  // of one step's alternative methods.
  // MUST stay above the early returns (rules-of-hooks).
  const previewSteps = useMemo(() => {
    const opByEntry = new Map((catalogue ?? []).map((e) => [e.id, e.operationId]));
    return steps
      .slice()
      .sort((a, b) => a.position - b.position)
      .map((step) => {
        const firstEnabled = step.apiBindings
          .filter((b) => b.isEnabled)
          .sort((a, b) => a.priority - b.priority)[0];
        const operationId = firstEnabled ? opByEntry.get(firstEnabled.catalogueEntryId) : undefined;
        // The PAN screens add a DOB field only when this step returns DOB/age.
        const dobRequired = step.returnFields.some(
          (f) => f.isIncluded && (f.fieldKey === 'date_of_birth' || f.fieldKey === 'age'),
        );
        return {
          id: step.id,
          stepType: step.stepType,
          // The ATTACHED QUESTIONNAIRE'S NAME WINS over `displayName` for a questionnaire step, and
          // the order matters: a step built before attaching started setting `displayName` still holds
          // the generic "Questionnaire", which is truthy — so putting `displayName` first meant the
          // attached name was never reached and the preview kept showing "Questionnaire" three times.
          // Same precedence `handleSave` uses, so the preview and what gets persisted agree.
          name: questionnaireDisplayName(step) ?? step.displayName ?? stepLabel(step.stepType),
          operationId,
          dobRequired,
          // Carried so a provider-less step (Questionnaire) can build its own preview screen
          // from what it has attached.
          settings: step.settings,
        };
      });
  }, [steps, catalogue]);

  // Welcome overview rows — every step, even ones whose method redirects out of
  // the app (Aadhaar DigiLocker) and so has no screen of its own. A step with no
  // API enabled shows 0 inputs.
  const previewWelcomeSteps = useMemo<WelcomePreviewStep[]>(() => {
    // Console's category per API, so the preview groups exactly the way the live
    // applicant screen will. Keyed on operationId — the catalogue's unique column.
    const categoryByOperation = new Map(
      (catalogue ?? []).map((e) => [e.operationId, e.category] as const),
    );

    return previewSteps.map((s) => ({
      id: s.id,
      stepType: s.stepType,
      name: s.name,
      // Provider-less steps have no catalogue API and so no category — grouped explicitly rather
      // than falling into "Other Verification", which would tell the applicant something is being
      // verified when they are only being asked questions (Questionnaire), or say nothing about the
      // one step that needs them to leave the page (Email Verification).
      //
      // Kept in step with `welcomeSteps.ts`, which does the same resolution for the real run — the
      // two lists must agree or the builder preview would group differently from what ships.
      category: s.operationId
        ? categoryByOperation.get(s.operationId)
        : s.stepType === 'questionnaire'
          ? QUESTIONNAIRE_CATEGORY
          : s.stepType === 'email_verification'
            ? EMAIL_VERIFICATION_CATEGORY
            : undefined,
    }));
  }, [previewSteps, catalogue]);

  // Journey screens — the subset of steps whose method has an in-app screen.
  // undefined until the catalogue resolves, so the preview can fall back.
  const previewJourney = useMemo<JourneyScreen[] | undefined>(() => {
    if (!catalogue) return undefined;
    return previewSteps.flatMap((s): JourneyScreen[] => {
      // Questionnaire calls no provider, so it has no operationId and the operationId filter
      // below would drop it — the journey would page straight past a step the applicant will
      // definitely see. It supplies its own screen instead.
      if (s.stepType === 'questionnaire') {
        return [
          {
            key: s.id,
            operationId: '',
            label: s.name,
            // The REAL form, not a summary card — see QuestionnaireStepPreview for why that changed.
            screens: [
              {
                node: (
                  <QuestionnaireStepPreview
                    questionnaireId={parseQuestionnaireStepSettings(s.settings).questionnaireId}
                    fallbackName={parseQuestionnaireStepSettings(s.settings).questionnaireName}
                  />
                ),
              },
            ],
          },
        ];
      }

      // Email Verification calls no provider either — WE send the code — so it supplies its own
      // screens for the same reason Questionnaire does. TWO of them, because the code cannot be
      // shown before the address is submitted; the step count stays one and the journey gains a
      // screen, exactly as with Face Match.
      if (s.stepType === 'email_verification') {
        return [
          {
            key: s.id,
            operationId: '',
            label: s.name,
            // Both screens say "Continue", exactly as the real run does — see the CTA note in
            // EmailStepRunner. A preview that showed "Send Code" / "Verify" would be advertising
            // buttons the applicant never sees.
            screens: [{ node: <EmailVerificationScreen /> }, { node: <EmailOtpScreen /> }],
          },
        ];
      }

      if (!s.operationId || !hasPreviewScreen(s.operationId)) {
        return [];
      }

      return [
        {
          key: s.id,
          operationId: s.operationId,
          dobRequired: s.dobRequired,
          // Captioned under the phone so the builder can see which step is on screen.
          label: s.name,
        },
      ];
    });
  }, [previewSteps, catalogue]);

  /**
   * Questionnaires attached to steps OTHER than the one being edited.
   *
   * A workflow may hold several Questionnaire steps, but two must not ask the same questionnaire:
   * the applicant would answer identical questions twice, and both steps would store those answers
   * under the same field ids, leaving the session with two indistinguishable copies. Computed here
   * because the editor is the only place that can see the sibling steps.
   */
  const takenQuestionnaireIds = useMemo(() => {
    const selectedId = selectedStepIdx !== null ? steps[selectedStepIdx]?.id : undefined;
    return steps
      .filter((s) => s.stepType === 'questionnaire' && s.id !== selectedId)
      .map((s) => parseQuestionnaireStepSettings(s.settings).questionnaireId)
      .filter((id): id is string => id !== null);
  }, [steps, selectedStepIdx]);

  // Branding for the phone preview: this workflow's own override if it has one,
  // otherwise the organization default — the same resolution an applicant session
  // uses, so the builder shows the theme the applicant will actually see.
  const { data: orgCustomization } = useGetCustomization();
  const { data: workflowCustomization } = useGetWorkflowCustomization(workflowId ?? null);
  const previewBranding = useMemo(
    () =>
      parseCustomization(
        workflowCustomization?.customizationJson ?? orgCustomization?.customizationJson ?? null,
      ),
    [workflowCustomization, orgCustomization],
  );
  const previewBrandStyle = useMemo(() => toBrandCssVars(previewBranding), [previewBranding]);
  // Welcome copy for the preview — the Settings overrides when enabled, else
  // undefined so WelcomeScreen falls back to its defaults.
  const welcomeCopy = resolveWelcomeCopy(workflowSettings);
  // The typography is a Google Font — without loading it the preview silently
  // falls back to sans-serif, so a font change would look like it did nothing.
  useGoogleFont(previewBranding.typographyStyle);

  // Selecting a step focuses the phone on that step's screen. The screen list now
  // spans the whole journey rather than the selected step's methods, so without
  // this the preview would stop following what's being edited.
  const selectedStepId = selectedStepIdx !== null ? steps[selectedStepIdx]?.id : undefined;
  // Read through a ref so only the SELECTION moves the preview: previewJourney gets
  // a new identity on every step edit, and depending on it would yank the phone back
  // to the selected step on each keystroke in the config panel.
  const previewJourneyRef = useRef(previewJourney);
  previewJourneyRef.current = previewJourney;
  // The editor auto-selects the first step on load, which would otherwise focus
  // the phone on that step's screen straight away. The builder should open on the
  // welcome screen (screen 0), so the FIRST selection is ignored and only later
  // ones — a real click in the sidebar — move the preview.
  const isInitialStepSelection = useRef(true);
  useEffect(() => {
    if (!selectedStepId) return;
    if (isInitialStepSelection.current) {
      isInitialStepSelection.current = false;
      return;
    }
    // Its FIRST screen — a step with two (Face Match, Liveness) opens on the instructions, and
    // Continue moves on to the camera, which is the order the applicant meets them in.
    const idx = firstScreenIndexForStep(previewJourneyRef.current, selectedStepId);
    if (idx >= 0) setPreviewScreen(idx);
  }, [selectedStepId]);

  // Cross-step guards for the selected step's Return Data fields — a field can be
  // DISABLED (not toggle-able) when it depends on data an earlier step must return.
  // Today: Aadhaar "Name Match Score vs PAN" needs an earlier PAN step that returns
  // PAN Holder Name — without that there's nothing to compare against, so the toggle
  // is locked off with a reason until such a PAN step exists. (Declared before the
  // early returns below so the hook order stays stable.)
  const returnFieldGuards = useMemo<Record<string, string>>(() => {
    const sel = selectedStepIdx !== null ? steps[selectedStepIdx] : null;
    const guards: Record<string, string> = {};
    if (!sel) return guards;

    // Aadhaar "Name Match Score vs PAN" needs an earlier PAN step returning the name.
    if (/aadhaar/i.test(sel.stepType)) {
      const earlierPanReturnsName = steps.some(
        (s) =>
          /pan/i.test(s.stepType) &&
          s.position < sel.position &&
          s.returnFields.some((f) => f.fieldKey === 'pan_holder_name' && f.isIncluded),
      );
      if (!earlierPanReturnsName) {
        guards.name_match_score =
          'Add a PAN step before this one and enable its “PAN Holder Name” return field to use Name Match Score.';
      }
      return guards;
    }

    // Bank name-match compares the account holder name against the applicant's
    // verified PAN / Aadhaar name — so it needs an earlier PAN step returning
    // “PAN Holder Name” OR an earlier Aadhaar step returning “Name”. Without either
    // there's nothing to match against, so both name fields are locked off.
    if (/bank/i.test(sel.stepType)) {
      const earlierNameSource = steps.some(
        (s) =>
          s.position < sel.position &&
          ((/pan/i.test(s.stepType) &&
            s.returnFields.some((f) => f.fieldKey === 'pan_holder_name' && f.isIncluded)) ||
            (/aadhaar/i.test(s.stepType) &&
              s.returnFields.some((f) => f.fieldKey === 'name' && f.isIncluded))),
      );
      if (!earlierNameSource) {
        const reason =
          'Add a PAN or Aadhaar step before this one (returning the holder name) to use Name Match.';
        guards.name_match_score = reason;
        guards.name_matched_against = reason;
      }
      return guards;
    }

    // GST "Legal Name Match Score" compares the legal name of business against the
    // applicant's verified PAN name ONLY (no Aadhaar counterpart per spec) — so it
    // needs an earlier PAN step returning "PAN Holder Name".
    if (/gst/i.test(sel.stepType)) {
      const earlierPanReturnsName = steps.some(
        (s) =>
          /pan/i.test(s.stepType) &&
          s.position < sel.position &&
          s.returnFields.some((f) => f.fieldKey === 'pan_holder_name' && f.isIncluded),
      );
      if (!earlierPanReturnsName) {
        const reason =
          'Add a PAN step before this one and enable its “PAN Holder Name” return field to use Name Match.';
        guards.name_match_score = reason;
        guards.name_matched_against = reason;
      }
      return guards;
    }

    return guards;
  }, [selectedStepIdx, steps]);

  if (isLoading) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (isError || !workflow) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-3">
        <p className="text-sm text-gray66">Failed to load workflow.</p>
        <Link to="/workflows" className="text-sm text-primary hover:underline">
          Back to Workflows
        </Link>
      </div>
    );
  }

  const selectedStep = selectedStepIdx !== null ? steps[selectedStepIdx] : null;
  // Identity for the step list is the step id — the same type can appear twice
  // (several Document Template steps, one document each). Those rows are labelled
  // with their template's name so they can be told apart.
  // eSign is billed PER SIGNER, by each signer's method — so its price comes from the
  // step's configured signers, not from one catalogue row. Keyed by operationId
  // because the three methods (Aadhaar / Electronic / DSC) are separately priced.
  // Not memoised: this sits below an early return, where a hook can't go, and it is
  // three entries built beside a reduce that already runs every render.
  const esignPriceByOperationId = new Map(
    (catalogue ?? [])
      .filter((e) => e.tag === 'eSign API Suite' && e.costPerCall != null)
      .map((e) => [e.operationId, e.costPerCall as number]),
  );

  /** What the ⓘ says about an eSign step's cost, given how it is configured. */
  function describeEsignPricing(signers: { boxCount: number }[]): string {
    const signing = signers.filter((signer) => signer.boxCount > 0).length;

    if (signers.length === 0) return 'No signers yet — this step costs nothing';
    if (signing === 0) {
      return 'No signatures placed yet — a signer is billed once their signature is on the document';
    }
    return `${signing} signer${signing === 1 ? '' : 's'} signing, billed per signature method`;
  }

  const activeSteps = steps.map((s) => {
    if (s.stepType === 'document_template') {
      const templateName = parseTemplateStepSettings(s.settings).templateName;
      return {
        id: s.id,
        stepType: s.stepType,
        label: templateName ? `Template · ${templateName}` : undefined,
      };
    }

    if (s.stepType === 'questionnaire') {
      // The row reads as the QUESTIONNAIRE IT ASKS, and nothing else — no "Questionnaire · " prefix.
      // The icon beside it already says what kind of step this is, and the prefix cost ~15 characters
      // of a rail narrow enough that it pushed the actual name into an ellipsis: "Questionnaire ·
      // Personal Informati…". Since two Questionnaire steps are told apart ONLY by this label, the
      // part that identifies them is the part that has to survive.
      const { questionnaireName } = parseQuestionnaireStepSettings(s.settings);
      return {
        id: s.id,
        stepType: s.stepType,
        label: questionnaireName ?? undefined,
        // Free, and stated as such: the tile's ₹0.00 is real, not a placeholder waiting for the
        // tenant's catalogue price.
        price: 0,
        priceNote: 'Free — collects answers, calls no verification provider',
      };
    }

    if (s.stepType === 'esign_template') {
      // The row shows what THIS step costs — the sum of its signers' methods. The
      // catalogue price for the eSign tag is one method's price, so using it here
      // would show ₹25 (Aadhaar) for a step with a single electronic signer.
      const { signers } = parseEsignStepSettings(s.settings);
      return {
        id: s.id,
        stepType: s.stepType,
        price: esignStepPrice(signers, esignPriceByOperationId),
        priceNote: describeEsignPricing(signers),
      };
    }

    return { id: s.id, stepType: s.stepType };
  });

  // MAX real (non-null) price per catalogue tag — a tag can hold several APIs at
  // different prices (e.g. Aadhaar: DigiLocker 7 vs OCR/Secure-QR 10), so the session
  // cost shows the worst case. Falls back to STEP_TYPE_META.baseCost when a step has
  // no real catalogue entry yet.
  const priceByTag = new Map<string, number>();
  for (const entry of catalogue ?? []) {
    // Skip hidden/dormant APIs (isAvailable=false, e.g. OVSE) so their untouched
    // default price doesn't inflate the max above the APIs actually on offer.
    if (entry.costPerCall != null && entry.isAvailable) {
      priceByTag.set(entry.tag, Math.max(priceByTag.get(entry.tag) ?? 0, entry.costPerCall));
    }
  }
  const totalCost = steps
    .filter((s) => s.isEnabled)
    .reduce((sum, s) => {
      if (s.stepType === 'esign_template') {
        const { signers } = parseEsignStepSettings(s.settings);
        return sum + esignStepPrice(signers, esignPriceByOperationId);
      }

      const tag = STEP_TYPE_CATALOGUE_TAGS[s.stepType];
      const realPrice = tag ? priceByTag.get(tag) : undefined;
      return sum + (realPrice ?? STEP_TYPE_META[s.stepType]?.baseCost ?? 0);
    }, 0);

  return (
    <AppShell title="Workflows" hideTopbar noPadding>
      {/*
        h-dvh = exact viewport height
        overflow-hidden = absolutely no outer page scroll
      */}
      <div className="flex h-dvh flex-col overflow-hidden text-black-logo">
        {/* ── Header: 60px fixed ── */}
        <div className="flex h-[60px] shrink-0 items-center gap-3 border-b border-grayEF bg-white px-4">
          {/* Back to the list. The design uses a bare chevron here — the word
              "Workflows" was crowding the workflow's own name beside it. */}
          <Link
            to="/workflows"
            aria-label="Back to workflows"
            title="Back to workflows"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-black-logo transition-colors hover:bg-grayEF hover:text-primary"
          >
            <ChevronLeft className="h-5 w-5" />
          </Link>
          {/* Workflow identity, per the designer header: the name reads as plain
              text with a pencil that flips it into an input, followed by a
              dropdown for switching to another workflow. Name only — the design
              has no description field, and any existing description is preserved
              untouched on save (see workflowDescription). */}
          <div className="hidden min-w-0 items-center gap-2.5 md:flex">
            {isEditingName && !isView ? (
              <>
                <input
                  type="text"
                  // biome-ignore lint/a11y/noAutofocus: the pencil exists to put the cursor here
                  autoFocus
                  className="sc-input input-small w-[clamp(130px,12vw,200px)] shrink-0 border-primary"
                  placeholder="Workflow name"
                  value={workflowName}
                  onChange={(e) => {
                    setWorkflowName(e.target.value);
                    setIsDirty(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === 'Escape') setIsEditingName(false);
                  }}
                />
                <button
                  type="button"
                  onClick={() => setIsEditingName(false)}
                  aria-label="Done editing name"
                  title="Done"
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-primary transition-colors hover:bg-blue-tint"
                >
                  <Check className="h-4 w-4" />
                </button>
              </>
            ) : (
              <button
                type="button"
                disabled={isView}
                onClick={() => setIsEditingName(true)}
                title={isView ? undefined : 'Rename workflow'}
                className="group flex min-w-0 items-center gap-1.5 disabled:cursor-default"
              >
                <span className="truncate text-[length:var(--text-18)] font-semibold tracking-tight text-black-logo">
                  {workflowName || 'Untitled Workflow'}
                </span>
                {!isView && (
                  <Pencil className="h-4 w-4 shrink-0 text-gray66 transition-colors group-hover:text-primary" />
                )}
              </button>
            )}

            {/* Change Workflow — jump straight to another workflow's editor. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded-lg border border-placeholder px-3 py-1.5 text-[13px] font-normal tracking-wide text-gray30 transition-colors hover:bg-grayEF"
                >
                  <span>Change Workflow</span>
                  <ChevronDown className="h-3.5 w-3.5 text-gray66" />
                </button>
              </DropdownMenuTrigger>
              {/* The heading stays put while only the list scrolls — with a few
                  dozen workflows an unbounded menu ran off the bottom of the
                  screen. ~7 rows visible, the rest reachable by scrolling. */}
              <DropdownMenuContent align="start" className="w-56 p-1.5">
                <p className="border-b border-grayEF px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-placeholder">
                  Select Workflow
                </p>
                <div className="custom-scrollbar mt-1 max-h-[260px] overflow-y-auto">
                  {switchableWorkflows.length === 0 ? (
                    <p className="px-2 py-2 text-[13px] text-gray66">No other workflows yet.</p>
                  ) : (
                    switchableWorkflows.map((w) => (
                      <DropdownMenuItem
                        key={w.id}
                        onClick={() => navigate(editorHref(w))}
                        className={cn(
                          'flex cursor-pointer items-center justify-between gap-2 rounded-md px-2 py-1.5 text-[13px]',
                          w.id === workflowId
                            ? 'bg-blue-tint font-semibold text-primary'
                            : 'text-gray30',
                        )}
                      >
                        <span className="truncate">{w.name}</span>
                        {w.id === workflowId && <Check className="h-3.5 w-3.5 shrink-0" />}
                      </DropdownMenuItem>
                    ))
                  )}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="ms-auto flex shrink-0 items-center gap-2.5">
            {isView ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-grayEF px-3 py-1 text-xs font-medium text-gray66">
                <Eye className="h-3.5 w-3.5" />
                View only
              </span>
            ) : (
              // Autosave status — no manual Save button. Changes persist
              // automatically ~800ms after the last edit (see autosave effect).
              <div className="flex min-w-[76px] items-center justify-end gap-1.5 text-xs">
                {saveWorkflow.isPending ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-gray66" />
                    <span className="text-gray66">Saving…</span>
                  </>
                ) : isDirty && hasIncompleteRules ? (
                  <span className="text-amber-500">Finish the rule to save</span>
                ) : isDirty ? (
                  <span className="text-amber-500">Unsaved changes</span>
                ) : (
                  <>
                    <Check className="h-3.5 w-3.5 text-green00" />
                    <span className="text-gray66">Saved</span>
                  </>
                )}
              </div>
            )}
            {/* Share + lifecycle actions (design header): Copy link (opens the
                New Session drawer to generate a shareable link), Publish, Delete.
                Hidden in read-only View mode. */}
            {/* Order matches the designer header exactly: Copy link · Publish ·
                Customization · Settings · Delete. The three icon-only buttons use
                the designer's own artwork (assets/icons/), masked so each follows
                its button's text colour on hover. */}
            {!isView && workflowId && (
              <>
                <div className="h-6 w-px bg-grayEF" />
                <button
                  type="button"
                  onClick={() => copyLink.copy(workflow?.publicSlug ?? '')}
                  className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[length:var(--text-14)] font-medium text-primary transition-colors hover:bg-blue-tint"
                >
                  <LinkIcon className="h-4 w-4" />
                  Copy link
                </button>
                <button
                  type="button"
                  onClick={() =>
                    publishWorkflow.mutate(workflowId, {
                      onSuccess: () => toast.success('Workflow published'),
                      onError: () => toast.error('Failed to publish workflow'),
                    })
                  }
                  disabled={publishWorkflow.isPending}
                  className="btn btn-primary btn-small disabled:opacity-50"
                >
                  {publishWorkflow.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <MaskIcon src={publishIcon} className="h-4 w-4" />
                  )}
                  Publish
                </button>
              </>
            )}
            {/* Per-workflow Customization — opens a right-side drawer (like
                Settings), scoped directly to this workflow. No page redirect. */}
            <button
              type="button"
              onClick={() => setCustomizationOpen(true)}
              aria-label="Workflow customization"
              title="Workflow customization"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray66 transition-colors hover:bg-placeholder/15 hover:text-primary"
            >
              <MaskIcon src={customizationIcon} className="h-[18px] w-[18px]" />
            </button>
            <button
              type="button"
              onClick={() => setSettingsOpen(true)}
              aria-label="Workflow settings"
              title="Workflow settings"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray66 transition-colors hover:bg-placeholder/15 hover:text-primary"
            >
              <MaskIcon src={settingsIcon} className="h-[18px] w-[18px]" />
            </button>
            {!isView && workflowId && (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                aria-label="Delete workflow"
                title="Delete workflow"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-redBF transition-colors hover:bg-red-50"
              >
                <MaskIcon src={deleteIcon} className="h-[18px] w-[18px]" />
              </button>
            )}
          </div>
        </div>

        <WorkflowSettingsDrawer
          isOpen={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          publicSlug={workflow?.publicSlug ?? ''}
          workflowName={workflowName}
          onWorkflowNameChange={(name) => {
            setWorkflowName(name);
            setIsDirty(true);
          }}
          value={workflowSettings}
          onChange={(next) => {
            setWorkflowSettings(next);
            setIsDirty(true);
          }}
          readOnly={isView}
        />

        {workflowId && (
          <WorkflowCustomizationDrawer
            isOpen={customizationOpen}
            onClose={() => setCustomizationOpen(false)}
            workflowId={workflowId}
            workflowName={workflowName}
            readOnly={isView}
            journey={previewJourney}
            welcomeSteps={previewWelcomeSteps}
          />
        )}

        <ConfirmDialog
          open={confirmDelete}
          title={`Delete "${workflowName}"?`}
          description="This permanently deletes the workflow. This action cannot be undone."
          confirmLabel="Delete"
          destructive
          isPending={deleteWorkflow.isPending}
          onConfirm={() => {
            if (!workflowId) return;
            deleteWorkflow.mutate(workflowId, {
              onSuccess: () => {
                toast.success('Workflow deleted');
                navigate('/workflows');
              },
              onError: () => toast.error('Failed to delete workflow'),
            });
          }}
          onCancel={() => setConfirmDelete(false)}
        />

        {/*
          ── 3-panel body ──
          Fills the remaining height below the single 60px header.
          overflow-hidden guarantees zero body scroll
        */}
        {/*
          In View mode we DON'T blanket-disable the body: the user must still be
          able to switch step-settings tabs and click through existing steps to
          inspect them. Instead `readOnly` is threaded into the catalog and the
          settings panel, which disable only their editing controls (toggles,
          drag, add/remove, inputs) while keeping navigation alive.
        */}
        <div className="flex min-h-0 flex-1 overflow-hidden bg-white p-2">
          {/* ════════════════════════════════════════
              LEFT PANEL — steps list
              fixed width, full body height, internal scroll only on step list
          ════════════════════════════════════════ */}
          <div
            className="flex shrink-0 flex-col overflow-hidden bg-white pt-[clamp(12px,0.875vw,14px)]"
            style={{ width: 'clamp(220px,22vw,350px)' }}
          >
            <StepCatalog
              activeSteps={activeSteps}
              selectedStepId={selectedStep?.id ?? null}
              onSelectStep={handleSelectStep}
              onAddStepType={handleAddStepType}
              onRemoveStep={handleRemoveStep}
              onReorder={handleReorderSteps}
              totalCost={totalCost}
              readOnly={isView}
            />
          </div>

          {/* Vertical divider (designer flat-panel layout) */}
          <div className="hidden w-px shrink-0 self-stretch bg-grayEF lg:block" />

          {/* ════════════════════════════════════════
              MIDDLE PANEL — step settings
              flex-[2] = takes 2/3 of remaining space
          ════════════════════════════════════════ */}
          <div className="flex min-w-0 flex-[2_1_0%] flex-col overflow-hidden bg-white">
            {selectedStep ? (
              <StepSettingsPanel
                step={selectedStep}
                onUpdate={(patch) => {
                  if (selectedStepIdx === null) return;
                  setSteps((prev) =>
                    prev.map((s, i) => (i === selectedStepIdx ? { ...s, ...patch } : s)),
                  );
                  setIsDirty(true);
                }}
                onClose={() => setSelectedStepIdx(null)}
                onDelete={handleDeleteStep}
                onSave={handleSave}
                isSaving={saveWorkflow.isPending}
                isDirty={isDirty}
                showSave={false}
                readOnly={isView}
                // The preview now shows one screen per STEP, not one per method, so enabling an API
                // focuses the phone on the step being edited. The index the panel reports is about
                // methods and no longer applies.
                onPreviewApiToggled={() => {
                  const idx = firstScreenIndexForStep(previewJourney, selectedStep?.id);
                  if (idx >= 0) setPreviewScreen(idx);
                }}
                returnFieldGuards={returnFieldGuards}
                takenQuestionnaireIds={takenQuestionnaireIds}
              />
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-grayEF">
                <p className="text-sm font-medium text-gray30">
                  {steps.length === 0 ? 'No steps added yet' : 'No step selected'}
                </p>
                <p className="max-w-52 text-center text-xs text-gray66">
                  {steps.length === 0
                    ? 'Add a step from the left panel to begin configuring your workflow.'
                    : 'Select a step from the left panel to configure its details.'}
                </p>
              </div>
            )}
          </div>

          {/* Vertical divider (designer flat-panel layout). Goes with the preview —
              a divider against the window edge reads as a stray line. */}
          {!STEPS_WITHOUT_PREVIEW.has(selectedStep?.stepType ?? '') && (
            <div className="hidden w-px shrink-0 self-stretch bg-grayEF min-[1080px]:block" />
          )}

          {/* ════════════════════════════════════════
              RIGHT PANEL — phone preview
              fixed 250px, visible from 1080px
              uses justify-between so phone fills top, carousel pins bottom
          ════════════════════════════════════════ */}
          {/* The phone shows what the APPLICANT sees on this step. These two have no
              applicant screen to show — a document is generated server-side, and
              signature placement is an admin task — and both need the width for the
              document itself. */}
          {!STEPS_WITHOUT_PREVIEW.has(selectedStep?.stepType ?? '') && (
            <div
              className="hidden shrink-0 flex-col overflow-hidden min-[1080px]:flex"
              style={{ width: 'clamp(280px,30vw,528px)' }}
            >
              <MobilePreview
                screen={previewScreen}
                onScreenChange={setPreviewScreen}
                stepType={selectedStep?.stepType}
                journey={previewJourney}
                welcomeSteps={previewWelcomeSteps}
                welcomeHeading={welcomeCopy.heading}
                welcomeSubheading={welcomeCopy.subheading}
                logoUrl={orgCustomization?.logoUrl ?? null}
                brandStyle={previewBrandStyle}
              />
            </div>
          )}
          {/* right panel end */}
        </div>
      </div>
    </AppShell>
  );
}
